import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useDayBookReturn } from "@/lib/useDayBookReturn";
import axios from "axios";
import { useDateFilterVersion } from "@/lib/useDateFilterVersion";
import { getFYRange, fyRangeLabel } from "@/lib/fyRange";
import { GroupPicker } from "@/components/GroupPicker";
import { useEscStep } from "@/lib/escBack";
import { Bank, UploadSimple, Trash, DownloadSimple, FileText, ArrowClockwise, Warning, Link as LinkIcon, TrendUp, TrendDown, Scales, Plus, PencilSimple, LinkBreak, MagnifyingGlass, CheckCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fyStartYear = (d = new Date()) => (d.getMonth() + 1 >= 4 ? d.getFullYear() : d.getFullYear() - 1);
const fyLabel = (y) => `FY ${y}-${String((y + 1) % 100).padStart(2, "0")}`;

const CATEGORIES = [
  { k: "sale_receipt", l: "Sale Receipt", color: "bg-emerald-100 text-emerald-800" },
  { k: "purchase_payment", l: "Purchase Payment", color: "bg-amber-100 text-amber-800" },
  { k: "expense", l: "Expense", color: "bg-rose-100 text-rose-800" },
  { k: "transfer", l: "Transfer", color: "bg-slate-100 text-slate-700" },
  { k: "bank_charge", l: "Bank Charge", color: "bg-orange-100 text-orange-800" },
  { k: "other", l: "Other", color: "bg-blue-100 text-blue-800" },
];

const catMeta = (k) => CATEGORIES.find(c => c.k === k) || CATEGORIES[5];

export default function BankStatements() {
  useDayBookReturn();
  const fileRef = useRef(null);
  const [tab, setTab] = useState("transactions"); // transactions | outstanding | financials
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [uploadNotice, setUploadNotice] = useState("");
  const [statements, setStatements] = useState([]);
  const [activeStmt, setActiveStmt] = useState("all");
  const [month, setMonth] = useState("");
  // FY comes from the SIDEBAR selector (single source of truth for every tab)
  const fyR = getFYRange();
  const year = fyR.from_year || fyStartYear();
  const multiYear = (fyR.to_year || year) > year;
  const fyDisplay = fyRangeLabel(fyR);
  const [txns, setTxns] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loadingTxns, setLoadingTxns] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [reconcileMsg, setReconcileMsg] = useState("");

  const [outstanding, setOutstanding] = useState(null);
  const [pnl, setPnl] = useState(null);
  const [prevPnl, setPrevPnl] = useState(null);
  const [bs, setBs] = useState(null);
  const [otherExpenses, setOtherExpenses] = useState([]);
  const [pnlDetail, setPnlDetail] = useState(null);
  const [bsDetail, setBsDetail] = useState(null);
  const [showDetail, setShowDetail] = useState(true);
  const [expForm, setExpForm] = useState({ expense_date: new Date().toISOString().slice(0,10), category: "", amount: "", description: "" });
  const [ledgerKind, setLedgerKind] = useState("all");
  const [ledger, setLedger] = useState(null);
  const [suspense, setSuspense] = useState(null);
  const [allParties, setAllParties] = useState(null);
  const [partyDetail, setPartyDetail] = useState(null); // {party, events, totals}
  const [partyLoading, setPartyLoading] = useState(false);
  const [loadingLedger, setLoadingLedger] = useState(false);

  // ---- Expenses Ledger (Petrol, Rent, Machinery, etc.) ----
  const [expenseLedgers, setExpenseLedgers] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [expenseForm, setExpenseForm] = useState({ name: "", category: "other", under_group_id: "", under_group: "", linked_ledger_id: "", linked_ledger_name: "" });
  const [acctGroups, setAcctGroups] = useState([]);
  const [acctLedgers, setAcctLedgers] = useState([]);
  useEffect(() => { axios.get(`${API}/account-groups`).then((r) => setAcctGroups(r.data.groups || [])).catch(() => {}); }, []);
  useEffect(() => { axios.get(`${API}/account-ledgers`).then((r) => setAcctLedgers(r.data.ledgers || r.data.items || r.data || [])).catch(() => {}); }, []);
  const [expDetail, setExpDetail] = useState(null); // {id, data} — expanded expense head entries

  // ---- Purchase-party (supplier) edit — Under (Ledger Group) jaise Sundry Creditors ----
  const [supEdit, setSupEdit] = useState(null);
  const [supSaving, setSupSaving] = useState(false);
  const openSupplierEdit = (p) => {
    const isMaster = !String(p.id).startsWith("supplier:") && !String(p.id).startsWith("suspense:");
    setSupEdit({ id: p.id, name: p.name || "", gstin: p.gstin || "", group_id: "", group_name: "", isMaster });
  };
  const saveSupplierEdit = async () => {
    if (!supEdit) return;
    if (!(supEdit.name || "").trim()) { alert("Party ka naam likhiye"); return; }
    if (!supEdit.group_id) { alert("Select 'Under (Ledger Group)' (e.g. Sundry Creditors)"); return; }
    setSupSaving(true);
    try {
      if (supEdit.isMaster) {
        const all = await axios.get(`${API}/customers`);
        const c = (all.data || []).find((x) => x.id === supEdit.id) || {};
        await axios.put(`${API}/customers/${supEdit.id}`, { ...c, name: supEdit.name.trim(), gstin: supEdit.gstin || null, group_id: supEdit.group_id, group_name: supEdit.group_name, party_type: "purchase" });
      } else {
        await axios.post(`${API}/customers`, { name: supEdit.name.trim(), gstin: supEdit.gstin || null, phone: "", address: "", city: "", state: "", state_code: "", pincode: "", group_id: supEdit.group_id, group_name: supEdit.group_name, party_type: "purchase" });
      }
      setSupEdit(null);
      await fetchLedger();
    } catch (e) { alert(e.response?.data?.detail || "Save failed"); }
    finally { setSupSaving(false); }
  };

  const openExpenseDetail = async (eid) => {
    if (expDetail?.id === eid) { setExpDetail(null); return; }
    try {
      const r = await axios.get(`${API}/expense-ledgers/${eid}`, { params: year ? { year } : {} });
      setExpDetail({ id: eid, data: r.data });
    } catch (e) { alert(e.response?.data?.detail || "Failed to load entries"); }
  };

  const removeExpenseEntry = async (tid) => {
    if (!window.confirm("Remove this entry from the expense ledger and move it back to Suspense?")) return;
    try {
      await axios.put(`${API}/bank-transactions/${tid}/assign-ledger`, { kind: "suspense" });
      if (expDetail) {
        const r = await axios.get(`${API}/expense-ledgers/${expDetail.id}`, { params: year ? { year } : {} });
        setExpDetail({ id: expDetail.id, data: r.data });
      }
      await fetchLedger(); await fetchTxns(); await fetchSummary();
    } catch (e) { alert(e.response?.data?.detail || "Remove failed"); }
  };
  const [expenseSaving, setExpenseSaving] = useState(false);

  // ---- Reconciliation UX (Red/Green + edit-to-map + group-by) ----------
  const [groupBy, setGroupBy] = useState("day"); // "day" | "month" | "year" | "none"
  const [matchFilter, setMatchFilter] = useState("all"); // "all" | "matched" | "unmatched"
  const [mapTxn, setMapTxn] = useState(null); // txn being manually mapped
  const [mapKind, setMapKind] = useState("invoice");
  const [mapQuery, setMapQuery] = useState("");
  const [mapCandidates, setMapCandidates] = useState([]);
  const [mapSaving, setMapSaving] = useState(false);

  // ---- Bulk select + delete ---------------------------------------------
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const dfv = useDateFilterVersion();
  useEffect(() => { fetchStatements(); fetchSummary(); }, [dfv]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { fetchTxns(); }, [activeStmt, month, year, dfv]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { fetchSummary(); if (tab === "outstanding") fetchOutstanding(); if (tab === "financials") fetchFinancials(); if (tab === "ledger") fetchLedger(); }, [year, tab, month, ledgerKind, dfv]);
  // If a party ledger is currently open and the FY changes, auto-refresh it
  useEffect(() => {
    if (partyDetail && ledgerKind === "all") {
      openPartyDetail(partyDetail.party.id, year);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  const fetchLedger = async () => {
    setLoadingLedger(true);
    try {
      if (ledgerKind === "suspense") {
        const params = new URLSearchParams(); if (year && !multiYear) params.set("year", year); if (month) params.set("month", month);
        const r = await axios.get(`${API}/ledger/suspense?${params}`);
        setSuspense(r.data);
      } else if (ledgerKind === "all") {
        const params = new URLSearchParams(); if (year && !multiYear) params.set("year", year);
        const r = await axios.get(`${API}/ledger/all-parties?${params}`);
        setAllParties(r.data);
      } else if (ledgerKind === "expenses") {
        const r = await axios.get(`${API}/expense-ledgers`);
        setExpenseLedgers(r.data.items || []);
        setExpenseCategories(r.data.categories || []);
      } else {
        const params = new URLSearchParams(); params.set("year", year); if (month) params.set("month", month);
        const r = await axios.get(`${API}/ledger/${ledgerKind}?${params}`);
        setLedger(r.data);
      }
    } catch(e){ console.error(e); }
    finally { setLoadingLedger(false); }
  };

  const createExpenseLedger = async () => {
    const name = (expenseForm.name || "").trim();
    if (!name) { alert("Enter a name for the expense head (e.g. Petrol, Rent, Machinery)"); return; }
    if (!expenseForm.under_group_id) { alert("Select 'Under (Ledger Group)' — the group this expense head belongs to"); return; }
    setExpenseSaving(true);
    try {
      await axios.post(`${API}/expense-ledgers`, { name, category: expenseForm.under_group || "other", under_group_id: expenseForm.under_group_id || null, under_group: expenseForm.under_group || null, linked_ledger_id: expenseForm.linked_ledger_id || null, linked_ledger_name: expenseForm.linked_ledger_name || null });
      setExpenseForm({ name: "", category: "other", under_group_id: "", under_group: "", linked_ledger_id: "", linked_ledger_name: "" });
      await fetchLedger();
    } catch (e) { alert(e.response?.data?.detail || "Create failed"); }
    finally { setExpenseSaving(false); }
  };

  const addCustomCategory = async () => {
    const raw = window.prompt("Enter new category name (e.g. Machine Loan EMI):", "");
    if (raw === null) return;  // cancelled
    const trimmed = (raw || "").trim();
    if (trimmed.length < 2) { alert("Category name must be at least 2 characters"); return; }
    try {
      const r = await axios.post(`${API}/expense-categories`, { name: trimmed });
      const slug = r.data.name;
      // Refetch so the dropdown includes the new slug, then select it
      await fetchLedger();
      setExpenseForm((f) => ({ ...f, category: slug }));
    } catch (e) { alert(e.response?.data?.detail || "Failed to add category"); }
  };

  const deleteExpenseLedger = async (eid, name) => {
    if (!window.confirm(`Delete expense ledger "${name}"? Linked bank txns will be detached but preserved.`)) return;
    try {
      await axios.delete(`${API}/expense-ledgers/${eid}`);
      await fetchLedger();
    } catch (e) { alert(e.response?.data?.detail || "Delete failed"); }
  };

  // ---- Expense head inline edit (name / under group / linked ledger) ------
  const [expEdit, setExpEdit] = useState(null); // {id, name, under_group_id, under_group, linked_ledger_id, linked_ledger_name}
  const [expEditSaving, setExpEditSaving] = useState(false);
  // Esc = one step back: close inline edit / expense detail / party drill-down first
  useEscStep(() => {
    if (expEdit) { setExpEdit(null); return true; }
    if (expDetail) { setExpDetail(null); return true; }
    if (partyDetail) { setPartyDetail(null); return true; }
    return false;
  });
  const startExpenseEdit = (e) => setExpEdit({
    id: e.id, name: e.name || "",
    under_group_id: e.under_group_id || "", under_group: e.under_group || "",
    linked_ledger_id: e.linked_ledger_id || "", linked_ledger_name: e.linked_ledger_name || "",
  });
  const saveExpenseEdit = async () => {
    if (!expEdit) return;
    const name = (expEdit.name || "").trim();
    if (!name) { alert("Expense head ka naam khali nahi ho sakta"); return; }
    setExpEditSaving(true);
    try {
      await axios.put(`${API}/expense-ledgers/${expEdit.id}`, {
        name,
        category: expEdit.under_group || undefined,
        under_group_id: expEdit.under_group_id || "",
        under_group: expEdit.under_group || "",
        linked_ledger_id: expEdit.linked_ledger_id || "",
        linked_ledger_name: expEdit.linked_ledger_name || "",
      });
      setExpEdit(null);
      await fetchLedger();
    } catch (e) { alert(e.response?.data?.detail || "Update failed"); }
    finally { setExpEditSaving(false); }
  };

  // ---- Party-wise reconciliation (All Ledgers list) -----------------------
  const [partyReconcileMsg, setPartyReconcileMsg] = useState("");
  const [reconTarget, setReconTarget] = useState(null);      // {id, name} → modal open
  const [selParties, setSelParties] = useState(new Set());   // bulk-selected ledger ids (All Ledgers)
  const [selTxnIds, setSelTxnIds] = useState(new Set());     // bulk-selected bank txn ids (party detail / suspense)
  const toggleSelTxn = (id) => setSelTxnIds((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelParty = (id) => setSelParties((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const [reconPreview, setReconPreview] = useState(null);    // counts for chooser
  const [reconRunning, setReconRunning] = useState(null);    // target currently running
  const [reconResult, setReconResult] = useState(null);      // last run result
  const [expenseHeads, setExpenseHeads] = useState([]);      // expense ledgers list
  const [ledgerSearch, setLedgerSearch] = useState("");

  const openReconcileModal = async (pid, name) => {
    setReconTarget({ id: pid, name });
    setReconPreview(null); setReconResult(null); setLedgerSearch("");
    try {
      const params = year ? `?year=${year}` : "";
      const [pv, eh] = await Promise.all([
        axios.get(`${API}/ledger/party/${encodeURIComponent(pid)}/reconcile-preview${params}`),
        axios.get(`${API}/expense-ledgers`),
      ]);
      setReconPreview(pv.data);
      setExpenseHeads(Array.isArray(eh.data) ? eh.data : eh.data?.items || []);
    } catch (e) { setReconPreview({ error: e.response?.data?.detail || "Failed to load" }); }
  };

  // Settle ALL of the source party's unlinked entries INTO the clicked ledger
  const runReconcileTo = async (tkind, item) => {
    if (!reconTarget) return;
    if (!window.confirm(`"${reconTarget.name}" ki saari unlinked bank entries "${item.name}" ledger mein settle kar dein? (bill-wise + date-wise automatic)`)) return;
    setReconRunning(`${tkind}:${item.id}`); setReconResult(null);
    try {
      const sources = reconTarget.multi || [{ id: reconTarget.id, name: reconTarget.name }];
      const agg = { invoices_linked: 0, purchases_linked: 0, expenses_linked: 0, on_account: 0, duplicates_skipped: 0, unmatched_left: 0, target: "" };
      let d = null;
      for (const src of sources) {
        const params = new URLSearchParams();
        params.set("target_kind", tkind); params.set("target_id", item.id);
        if (year) params.set("year", year);
        const r = await axios.post(`${API}/ledger/party/${encodeURIComponent(src.id)}/reconcile-to?${params}`);
        d = r.data;
        agg.invoices_linked += d.invoices_linked || 0; agg.purchases_linked += d.purchases_linked || 0;
        agg.expenses_linked += d.expenses_linked || 0; agg.on_account += d.on_account || 0;
        agg.duplicates_skipped += d.duplicates_skipped || 0; agg.unmatched_left += d.unmatched_left || 0;
        agg.target = d.target;
      }
      setReconResult(agg);
      setPartyReconcileMsg(`${reconTarget.name} → ${agg.target}: ${agg.invoices_linked} sale bill, ${agg.purchases_linked} purchase bill, ${agg.expenses_linked} expense · ${agg.on_account || 0} on-account · ${agg.duplicates_skipped} repeated skipped`);
      setTimeout(() => setPartyReconcileMsg(""), 15000);
      setSelParties(new Set());
      await fetchLedger();
      if (!reconTarget.multi) {
        const pr = await axios.get(`${API}/ledger/party/${encodeURIComponent(reconTarget.id)}/reconcile-preview${year ? `?year=${year}` : ""}`);
        setReconPreview(pr.data);
      } else {
        setReconTarget(null);
      }
    } catch (e) { alert(e.response?.data?.detail || "Ledger settle failed"); }
    finally { setReconRunning(null); }
  };

  // Send ALL of the source party's bank entries back to Suspense (full reversal)
  const movePartyToSuspense = async () => {
    if (!reconTarget) return;
    if (!window.confirm(`"${reconTarget.name}" : move ALL bank entries to the Suspense tab? (all settlements will be reversed)`)) return;
    setReconRunning("suspense"); setReconResult(null);
    try {
      const sources = reconTarget.multi || [{ name: reconTarget.name }];
      let moved = 0;
      for (const src of sources) {
        const r = await axios.post(`${API}/ledger/party-to-suspense`, { party_name: src.name });
        moved += r.data.moved || 0;
      }
      setPartyReconcileMsg(`${reconTarget.name}: ${moved} entries moved to Suspense`);
      setTimeout(() => setPartyReconcileMsg(""), 15000);
      setReconTarget(null); setSelParties(new Set());
      await fetchLedger(); await fetchTxns(); await fetchSummary();
    } catch (e) { alert(e.response?.data?.detail || "Suspense move failed"); }
    finally { setReconRunning(null); }
  };

  const openPartyDetail = async (partyId, useYear = year) => {
    setPartyLoading(true); setPartyDetail(null);
    try {
      const url = useYear ? `${API}/ledger/party/${encodeURIComponent(partyId)}?year=${useYear}`
                          : `${API}/ledger/party/${encodeURIComponent(partyId)}`;
      const r = await axios.get(url);
      setPartyDetail(r.data);
    } catch (e) { alert(e.response?.data?.detail || "Failed to load party ledger"); }
    finally { setPartyLoading(false); }
  };

  // Auto-poll any statements still processing
  useEffect(() => {
    const pending = statements.filter(s => s.processing_status === "pending" || s.processing_status === "processing");
    if (pending.length === 0) return;
    const t = setTimeout(() => { fetchStatements(); fetchTxns(); fetchSummary(); }, 3000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statements]);

  const fetchOutstanding = async () => {
    try { const r = await axios.get(`${API}/reconciliation/outstanding`); setOutstanding(r.data); } catch(e){console.error(e);}
  };
  const fetchFinancials = async () => {
    try {
      const params = new URLSearchParams(); params.set("year", year); if (month) params.set("month", month);
      const [p, b, e, pd, bd, pp] = await Promise.all([
        axios.get(`${API}/financials/pnl?${params}`),
        axios.get(`${API}/financials/balance-sheet`),
        axios.get(`${API}/other-expenses?${params}`),
        axios.get(`${API}/financials/pnl/detail?${params}`),
        axios.get(`${API}/financials/balance-sheet/detail`),
        axios.get(`${API}/financials/pnl?year=${year - 1}`),
      ]);
      setPnl(p.data); setBs(b.data); setOtherExpenses(e.data); setPnlDetail(pd.data); setBsDetail(bd.data); setPrevPnl(pp.data);
    } catch (err) { console.error(err); }
  };

  const handleReconcile = async () => {
    setReconciling(true); setReconcileMsg("");
    try {
      const res = await axios.post(`${API}/bank-transactions/reconcile`);
      setReconcileMsg(`Matched ${res.data.matched}, auto-settled ${res.data.auto_settled || 0} — ${res.data.still_red ?? res.data.unmatched} still unmatched${res.data.ai_matched ? ` (${res.data.ai_matched} via AI)` : ""}`);
      await fetchTxns(); await fetchOutstanding();
    } catch (e) { setReconcileMsg("Reconcile failed"); }
    finally { setReconciling(false); setTimeout(() => setReconcileMsg(""), 5000); }
  };

  const handleAddExpense = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/other-expenses`, { ...expForm, amount: parseFloat(expForm.amount) || 0 });
      setExpForm({ expense_date: new Date().toISOString().slice(0,10), category: "", amount: "", description: "" });
      await fetchFinancials();
    } catch (err) { alert("Failed to add expense"); }
  };

  const handleDeleteExpense = async (eid) => {
    if (!window.confirm("Delete this expense?")) return;
    try { await axios.delete(`${API}/other-expenses/${eid}`); await fetchFinancials(); }
    catch (err) { alert("Delete failed"); }
  };

  const downloadFile = async (url, filename) => {
    try {
      const res = await axios.get(url, { responseType: "blob" });
      const objUrl = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a"); link.href = objUrl; link.setAttribute("download", filename);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(objUrl);
    } catch (err) { alert("Download failed: " + (err.response?.data?.detail || err.message)); }
  };

  const fetchStatements = async () => {
    try {
      const res = await axios.get(`${API}/bank-statements`);
      setStatements(res.data);
    } catch (e) { console.error(e); }
  };

  const fetchSummary = async () => {
    try {
      const res = await axios.get(`${API}/bank-transactions/summary${multiYear ? "" : `?year=${year}`}`);
      setSummary(res.data);
    } catch (e) { console.error(e); }
  };

  const fetchTxns = async () => {
    setLoadingTxns(true);
    try {
      const params = new URLSearchParams();
      if (activeStmt !== "all") params.set("statement_id", activeStmt);
      if (month) params.set("month", month);
      else if (year && !multiYear) params.set("year", year);
      const res = await axios.get(`${API}/bank-transactions?${params}`);
      setTxns(res.data);
      setSelectedIds(new Set());
    } catch (e) { console.error(e); }
    finally { setLoadingTxns(false); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setUploadError("");
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await axios.post(`${API}/bank-statements/upload`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
      // Upload is queued - refresh statements list immediately (pending badge appears)
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
      await fetchStatements();
      // Start polling this statement's status
      pollStatementStatus(res.data.statement_id);
    } catch (err) {
      setUploadError(err.response?.data?.detail || "Upload failed");
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const pollStatementStatus = async (sid) => {
    for (let i = 0; i < 40; i++) {
      try {
        const r = await axios.get(`${API}/bank-statements/${sid}/status`);
        await fetchStatements();
        if (r.data.status === "completed") {
          await fetchTxns(); await fetchSummary();
          // Show a summary banner: how many rows imported / skipped as duplicate
          const stmts = await axios.get(`${API}/bank-statements`);
          const stmt = (stmts.data || []).find((x) => x.id === sid);
          const dup = Number(stmt?.duplicates_skipped || 0);
          const cnt = Number(stmt?.transaction_count || 0);
          if (dup > 0) {
            setUploadNotice(`Statement processed — ${cnt} new entries added, ${dup} duplicate ${dup === 1 ? "entry" : "entries"} skipped (date + amount + reference/description match).`);
          } else if (cnt > 0) {
            setUploadNotice(`Statement processed — ${cnt} new entries added. No duplicates found.`);
          } else {
            setUploadNotice(`Statement processed — 0 new entries added; every row in this file was already imported earlier.`);
          }
          setTimeout(() => setUploadNotice(""), 12000);
          return;
        }
        if (r.data.status === "failed") {
          setUploadError(`Extraction failed: ${r.data.error || "unknown error"}`);
          return;
        }
      } catch (e) {
        // Polling can fail transiently (network blip, 429, brief auth refresh). Log so
        // we have visibility without breaking the UX — next tick will retry.
        console.warn("Bank statement poll failed; retrying in 3s", e?.message || e);
      }
      await new Promise((res) => setTimeout(res, 3000));
    }
  };

  const handleDelete = async (sid) => {
    if (!window.confirm("Delete this statement and all its transactions?")) return;
    try {
      await axios.delete(`${API}/bank-statements/${sid}`);
      if (activeStmt === sid) setActiveStmt("all");
      await fetchStatements(); await fetchTxns(); await fetchSummary();
    } catch (e) { alert(e.response?.data?.detail || "Delete failed"); }
  };

  const handleExport = async () => {
    const params = new URLSearchParams();
    if (activeStmt !== "all") params.set("statement_id", activeStmt);
    if (month) params.set("month", month);
    else if (year) params.set("year", year);
    const res = await axios.get(`${API}/bank-transactions/export/excel?${params}`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement("a"); link.href = url;
    link.setAttribute("download", `Transactions_${month || year}.xlsx`);
    document.body.appendChild(link); link.click(); link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleUpdateCategory = async (tid, newCat) => {
    try {
      await axios.put(`${API}/bank-transactions/${tid}`, { category: newCat });
      setTxns(prev => prev.map(t => t.id === tid ? { ...t, category: newCat } : t));
    } catch (e) { alert("Update failed"); }
  };

  // ---- Manual map / unmap helpers -----------------------------------------
  const openMapDialog = (txn) => {
    setMapTxn(txn);
    setMapKind("party");
    setMapQuery(""); setMapCandidates([]);
    setTimeout(() => searchMapCandidates("party", ""), 50);
  };
  const searchMapCandidates = async (kind, q) => {
    try {
      let all = [];
      if (kind === "expense") {
        const r = await axios.get(`${API}/expense-ledgers`);
        all = (r.data.items || []).map((x) => ({ ...x, _display: x.name, _sub: `(${(x.category || "other").replace(/_/g, " ")})`, _amount: x.spent || 0 }));
      } else {
        const r = await axios.get(`${API}/ledger/all-parties`, { params: { year } });
        all = (r.data.parties || []);
      }
      const term = (q || "").toLowerCase().trim();
      const filtered = term
        ? all.filter((d) => (d.name || d._display || "").toLowerCase().includes(term) || (d.category || "").includes(term))
        : all;
      setMapCandidates(filtered.slice(0, 30));
    } catch { setMapCandidates([]); }
  };
  const applyLedger = async (kind, doc) => {
    if (!mapTxn) return;
    setMapSaving(true);
    try {
      let r;
      if (mapTxn.bulk) {
        const body = kind === "party"
          ? { txn_ids: mapTxn.bulk, kind: "party", ledger_id: doc.id, ledger_name: doc.name }
          : kind === "expense"
            ? { txn_ids: mapTxn.bulk, kind: "expense", ledger_id: doc.id }
            : { txn_ids: mapTxn.bulk, kind: "suspense" };
        r = await axios.put(`${API}/bank-transactions/bulk-assign-ledger`, body);
        setMapTxn(null); setSelTxnIds(new Set());
        const d = r.data || {};
        alert(`✓ ${d.processed} entries reconciled${d.ledger ? ` → ${d.ledger}` : " → Suspense"} — ${d.settled_bills} bill(s) settled${d.on_account > 0 ? `, ₹${d.on_account.toLocaleString()} on-account` : ""}${d.failed ? `, ${d.failed} failed` : ""}`);
      } else {
        const body = kind === "party"
          ? { kind: "party", ledger_id: doc.id, ledger_name: doc.name }
          : kind === "expense"
            ? { kind: "expense", ledger_id: doc.id }
            : { kind: "suspense" };
        r = await axios.put(`${API}/bank-transactions/${mapTxn.id}/assign-ledger`, body);
        setMapTxn(null);
        const d = r.data || {};
        if (d.message === "linked_party") {
          alert(`✓ ${d.ledger} ledger me settle hua — ${d.settled_bills} bill(s) linked${d.on_account > 0 ? `, ₹${d.on_account.toLocaleString()} on-account` : ""}`);
        } else if (d.message === "moved_to_suspense") {
          alert("✓ Entry moved to the Suspense tab");
        }
      }
      await fetchTxns(); await fetchSummary();
      if (tab === "ledger") await fetchLedger();
      if (partyDetail) await openPartyDetail(partyDetail.party.id, year);
      if (expDetail) {
        try { const rr = await axios.get(`${API}/expense-ledgers/${expDetail.id}`, { params: year ? { year } : {} }); setExpDetail({ id: expDetail.id, data: rr.data }); } catch {}
      }
    } catch (e) { alert(e.response?.data?.detail || "Reconcile failed"); }
    finally { setMapSaving(false); }
  };
  const unlinkTxn = async (txn) => {
    if (!window.confirm("Unlink this transaction from its matched invoice/purchase/expense?")) return;
    try {
      await axios.delete(`${API}/bank-transactions/${txn.id}/link`);
      await fetchTxns(); await fetchSummary();
      if (tab === "ledger") await fetchLedger();
    } catch { alert("Unlink failed"); }
  };

  const totCredit = txns.reduce((s, t) => s + (t.credit || 0), 0);
  const totDebit = txns.reduce((s, t) => s + (t.debit || 0), 0);

  // ---- Bulk select / delete helpers --------------------------------------
  const toggleSelect = (id) => setSelectedIds((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} selected ${ids.length === 1 ? "entry" : "entries"}? Settled entries' payments will also be reversed.`)) return;
    setBulkDeleting(true);
    try {
      const r = await axios.post(`${API}/bank-transactions/bulk-delete`, { ids });
      setSelectedIds(new Set());
      setUploadNotice(`${r.data.deleted} entries deleted${r.data.invoices_adjusted ? ` · ${r.data.invoices_adjusted} invoice payment(s) reversed` : ""}.`);
      setTimeout(() => setUploadNotice(""), 8000);
      await fetchTxns(); await fetchSummary();
    } catch (e) { alert(e.response?.data?.detail || "Bulk delete failed"); }
    finally { setBulkDeleting(false); }
  };

  // ---- Grouping for display: bucket by day / month / year --------------
  // A txn is "matched" (green) if it's linked to an invoice, purchase,
  // expense head OR auto-settled to a party ledger. Else "unmatched" (red).
  const isMatched = (t) => !!(t.reconciled_invoice_id || t.reconciled_purchase_id || t.reconciled_expense_id || t.auto_settled);
  // Success/Suspense tabs force the match filter
  const effFilter = tab === "success" ? "matched" : tab === "suspense" ? "unmatched" : matchFilter;
  const groupedTxns = useMemo(() => {
    const filtered = txns.filter((t) => {
      if (effFilter === "matched") return isMatched(t);
      if (effFilter === "unmatched") return !isMatched(t);
      return true;
    });
    if (groupBy === "none") return [{ key: "All", label: "All Transactions", rows: filtered }];
    const buckets = new Map();
    for (const t of filtered) {
      const d = String(t.txn_date || "").slice(0, 10);
      let key = "Unknown", label = "Unknown";
      if (d) {
        if (groupBy === "year")       { key = d.slice(0, 4); label = key; }
        else if (groupBy === "month") { key = d.slice(0, 7); const [y, m] = key.split("-"); label = new Date(y, parseInt(m) - 1).toLocaleString("en-IN", { month: "long", year: "numeric" }); }
        else                          { key = d; label = new Date(d).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" }); }
      }
      if (!buckets.has(key)) buckets.set(key, { key, label, rows: [] });
      buckets.get(key).rows.push(t);
    }
    return Array.from(buckets.values()).sort((a, b) => (a.key < b.key ? 1 : -1)); // newest first
  }, [txns, groupBy, effFilter]);

  const visibleRows = useMemo(() => groupedTxns.flatMap((g) => g.rows), [groupedTxns]);
  const allSelected = visibleRows.length > 0 && visibleRows.every((t) => selectedIds.has(t.id));
  const toggleSelectAll = () => setSelectedIds(allSelected ? new Set() : new Set(visibleRows.map((t) => t.id)));

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto" data-testid="bank-page">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tighter text-slate-900 mb-1 flex items-center gap-3">
            <Bank size={36} weight="duotone" className="text-blue-600" /> Bank & Reconciliation
          </h1>
          <p className="text-slate-500 text-sm">Upload bank statements (PDF, Excel or CSV). AI extracts every transaction, auto-categorises them, and matches them to your invoices.</p>
        </div>
        <div>
          <input ref={fileRef} type="file" accept="application/pdf,.pdf,.xlsx,.csv" onChange={handleUpload} className="hidden" data-testid="bank-file-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-statement-btn" className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2">
            {uploading ? (<><ArrowClockwise size={18} className="animate-spin" /> Queuing…</>) : (<><UploadSimple size={18} weight="bold" /> Upload Statement (PDF/Excel)</>)}
          </Button>
          {uploadError && <p className="text-xs text-red-600 mt-1 flex items-center gap-1" data-testid="upload-error"><Warning size={12} weight="fill" /> {uploadError}</p>}
          {uploadNotice && (
            <p className="text-xs text-emerald-800 mt-1 flex items-center gap-1 bg-emerald-50 border border-emerald-200 rounded-sm px-2 py-1 max-w-md" data-testid="upload-notice">
              <CheckCircle size={12} weight="fill" className="text-emerald-600 shrink-0" /> {uploadNotice}
            </p>
          )}
          {statements.some(s => s.processing_status === "pending" || s.processing_status === "processing") && (
            <p className="text-xs text-blue-600 mt-1 flex items-center gap-1" data-testid="processing-indicator">
              <ArrowClockwise size={12} className="animate-spin" /> AI is extracting transactions in the background — you can keep working.
            </p>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200 mb-4 flex-wrap">
        {[{k:"transactions",l:"Transactions"},{k:"success",l:"✓ Success"},{k:"suspense",l:"! Suspense"},{k:"ledger",l:"Bank Ledger"},{k:"outstanding",l:"Outstanding (Paid vs Unpaid)"},{k:"financials",l:"P&L / Balance Sheet"}].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`bank-tab-${t.k}`} className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-all ${tab === t.k ? "border-blue-600 text-blue-600" : "border-transparent text-slate-600 hover:text-slate-900"}`}>
            {t.l}
          </button>
        ))}
      </div>

      {(tab === "transactions" || tab === "success" || tab === "suspense") && (<>
      {/* Yearly summary cards */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <p className="text-xs font-bold uppercase text-slate-500">Credits {fyDisplay}</p>
            <p className="text-2xl font-black text-emerald-700">₹{summary.total_credit.toLocaleString()}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <p className="text-xs font-bold uppercase text-slate-500">Debits {fyDisplay}</p>
            <p className="text-2xl font-black text-rose-700">₹{summary.total_debit.toLocaleString()}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <p className="text-xs font-bold uppercase text-slate-500">Net</p>
            <p className={`text-2xl font-black ${summary.net >= 0 ? "text-emerald-700" : "text-rose-700"}`}>₹{summary.net.toLocaleString()}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <p className="text-xs font-bold uppercase text-slate-500">Statements</p>
            <p className="text-2xl font-black text-slate-900">{statements.length}</p>
          </div>
        </div>
      )}

      {/* Monthly breakdown */}
      {summary && summary.monthly.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-sm p-4 mb-6">
          <p className="text-xs font-bold uppercase text-slate-500 mb-3">Monthly breakdown {fyLabel(summary.year)}</p>
          <div className="grid grid-cols-2 sm:grid-cols-6 gap-2">
            {summary.monthly.map(m => (
              <button key={m.month} onClick={() => setMonth(m.month)} data-testid={`month-pill-${m.month}`} className={`text-left p-2 rounded-sm border text-xs transition-all ${month === m.month ? "bg-blue-50 border-blue-500" : "border-slate-200 hover:bg-slate-50"}`}>
                <p className="font-bold text-slate-900">{m.month}</p>
                <p className="text-emerald-700">+₹{m.credit.toLocaleString()}</p>
                <p className="text-rose-700">-₹{m.debit.toLocaleString()}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Statements list */}
      {statements.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-sm p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-bold uppercase text-slate-500">Uploaded Statements</p>
            <Button size="sm" onClick={handleReconcile} disabled={reconciling} data-testid="reconcile-btn" className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-1">
              {reconciling ? <><ArrowClockwise size={14} className="animate-spin" /> Settling…</> : <><LinkIcon size={14} /> Auto-Settle All</>}
            </Button>
          </div>
          {reconcileMsg && <p className="text-xs text-emerald-700 mb-2">{reconcileMsg}</p>}
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setActiveStmt("all")} data-testid="stmt-pill-all" className={`px-3 py-1.5 rounded-sm text-xs font-semibold border ${activeStmt === "all" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}>All ({statements.reduce((s, x) => s + (x.transaction_count || 0), 0)})</button>
            {statements.map(s => {
              const st = s.processing_status || "completed";
              const isProc = st === "pending" || st === "processing";
              const isFail = st === "failed";
              return (
              <div key={s.id} className={`flex items-center gap-1 border rounded-sm ${isFail ? "border-rose-300 bg-rose-50" : isProc ? "border-blue-300 bg-blue-50" : activeStmt === s.id ? "border-blue-500 bg-blue-50" : "border-slate-200"}`}>
                <button onClick={() => !isProc && setActiveStmt(s.id)} disabled={isProc} data-testid={`stmt-pill-${s.id}`} className="px-3 py-1.5 text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                  {isProc && <ArrowClockwise size={12} className="animate-spin text-blue-600" />}
                  {isFail && <Warning size={12} weight="fill" className="text-rose-600" />}
                  <span className="font-bold">{s.bank_name || s.file_name}</span>
                  {!isProc && !isFail && <span className="text-slate-500">{s.statement_period_start} → {s.statement_period_end}</span>}
                  {isProc && <span className="text-blue-700">{s.processing_progress || "Processing…"}</span>}
                  {isFail && <span className="text-rose-700" title={s.processing_error}>Failed</span>}
                  {!isProc && <span className="text-slate-400">({s.transaction_count || 0})</span>}
                  {!isProc && Number(s.duplicates_skipped || 0) > 0 && (
                    <span className="text-amber-700 bg-amber-100 border border-amber-300 rounded-sm px-1.5 text-[10px] font-bold" title="Duplicate rows detected on upload and skipped">
                      {s.duplicates_skipped} dup skipped
                    </span>
                  )}
                </button>
                <button onClick={() => handleDelete(s.id)} className="px-2 py-1.5 text-rose-500 hover:bg-rose-50 rounded-sm" title="Delete"><Trash size={14} /></button>
              </div>);
            })}
          </div>
        </div>
      )}

      {/* Filters + transactions */}
      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            <div>
              <Label className="text-xs">Month</Label>
              <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40" data-testid="filter-month" />
            </div>
            <div>
              <Label className="text-xs">Financial Year (from sidebar)</Label>
              <div className="h-9 px-3 border border-slate-200 rounded-sm text-sm bg-slate-50 font-bold flex items-center text-slate-700" data-testid="filter-year-display" title="LokTech Dex Pro ke naam ke neeche wale calendar se FY change karein — sabhi tabs par wahi FY lagta hai">
                {fyDisplay}
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => { setMonth(""); setActiveStmt("all"); }} data-testid="reset-filters">Reset</Button>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-xs">
              <span className="text-emerald-700 font-bold">+₹{totCredit.toLocaleString()}</span>
              <span className="text-slate-300 mx-2">|</span>
              <span className="text-rose-700 font-bold">-₹{totDebit.toLocaleString()}</span>
            </div>
            <Button onClick={handleExport} disabled={txns.length === 0} data-testid="export-txns-btn" className="bg-green-600 hover:bg-green-700 text-white flex items-center gap-1" size="sm">
              <DownloadSimple size={16} /> Excel
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto sticky-table">
          {/* Group / filter toolbar */}
          <div className="flex items-center gap-2 flex-wrap px-3 py-2 bg-slate-50 border-b border-slate-200">
            <span className="text-[11px] font-bold uppercase text-slate-500 mr-1">Group by:</span>
            {[
              { k: "day",   l: "Day"   },
              { k: "month", l: "Month" },
              { k: "year",  l: "Year"  },
              { k: "none",  l: "None"  },
            ].map((g) => (
              <button key={g.k} data-testid={`txn-groupby-${g.k}`}
                onClick={() => setGroupBy(g.k)}
                className={`px-2.5 py-1 text-[11px] font-bold rounded-sm transition-colors ${groupBy === g.k ? "bg-slate-900 text-white" : "bg-white border border-slate-300 text-slate-600 hover:border-slate-500"}`}>
                {g.l}
              </button>
            ))}
            {tab === "transactions" && (<>
            <span className="text-[11px] font-bold uppercase text-slate-500 ml-4 mr-1">Show:</span>
            {[
              { k: "all",       l: "All",       cls: "" },
              { k: "matched",   l: "✓ Matched",  cls: "text-emerald-700" },
              { k: "unmatched", l: "! Unmatched", cls: "text-rose-700" },
            ].map((f) => (
              <button key={f.k} data-testid={`txn-filter-${f.k}`}
                onClick={() => setMatchFilter(f.k)}
                className={`px-2.5 py-1 text-[11px] font-bold rounded-sm transition-colors ${matchFilter === f.k ? "bg-slate-900 text-white" : `bg-white border border-slate-300 hover:border-slate-500 ${f.cls}`}`}>
                {f.l}
              </button>
            ))}</>)}
            {tab === "success" && <span className="text-[11px] font-bold uppercase text-emerald-700 ml-4">Successfully reconciled entries</span>}
            {tab === "suspense" && <span className="text-[11px] font-bold uppercase text-rose-700 ml-4">Unassigned — no name / cash entries. Map manually.</span>}
            <div className="ml-auto flex items-center gap-3">
              {selectedIds.size > 0 && (
                <button onClick={handleBulkDelete} disabled={bulkDeleting} data-testid="bulk-delete-btn"
                  className="inline-flex items-center gap-1 px-3 py-1 bg-rose-600 hover:bg-rose-700 text-white rounded-sm text-[11px] font-bold disabled:opacity-60">
                  <Trash size={12} weight="bold" /> {bulkDeleting ? "Deleting…" : `Delete Selected (${selectedIds.size})`}
                </button>
              )}
              <div className="text-[11px] text-slate-500 font-semibold">
                <span className="text-emerald-700">{txns.filter(isMatched).length} matched</span>
                <span className="text-slate-300 mx-1.5">·</span>
                <span className="text-rose-700">{txns.filter((t) => !isMatched(t)).length} unmatched</span>
              </div>
            </div>
          </div>

          <table className="w-full text-sm">
            <thead className="sticky-thead bg-slate-50 border-b border-slate-200 sticky top-0">
              <tr className="text-xs font-bold uppercase text-slate-600">
                <th className="px-3 py-2 text-left w-8">
                  <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} data-testid="txn-select-all" className="cursor-pointer h-4 w-4" title="Select all visible entries" />
                </th>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-left">Description</th>
                <th className="px-3 py-2 text-left">Party</th>
                <th className="px-3 py-2 text-right">Debit</th>
                <th className="px-3 py-2 text-right">Credit</th>
                <th className="px-3 py-2 text-left">Category</th>
                <th className="px-3 py-2 text-left">Matched</th>
                <th className="px-3 py-2 text-left">Action</th>
              </tr>
            </thead>
            <tbody>
              {loadingTxns ? (
                <tr><td colSpan={9} className="px-3 py-10 text-center text-slate-400 text-sm">Loading transactions…</td></tr>
              ) : txns.length === 0 ? (
                <tr><td colSpan={9} className="px-3 py-10 text-center">
                  <FileText size={36} weight="duotone" className="mx-auto text-slate-300 mb-2" />
                  <p className="text-slate-500 text-sm">No transactions yet. Upload a bank statement PDF to get started.</p>
                </td></tr>
              ) : (
                groupedTxns.map((group) => {
                  const gCredit = group.rows.reduce((s, x) => s + (x.credit || 0), 0);
                  const gDebit  = group.rows.reduce((s, x) => s + (x.debit || 0), 0);
                  const gMatched = group.rows.filter(isMatched).length;
                  return (
                    <Fragment key={group.key}>
                      {groupBy !== "none" && (
                        <tr key={`h-${group.key}`} className="bg-slate-100 border-y-2 border-slate-300" data-testid={`txn-group-${group.key}`}>
                          <td colSpan={4} className="px-3 py-2 text-xs font-black uppercase tracking-wide text-slate-700">{group.label}</td>
                          <td className="px-3 py-2 text-right font-mono text-xs text-rose-700 font-bold">-₹{gDebit.toLocaleString()}</td>
                          <td className="px-3 py-2 text-right font-mono text-xs text-emerald-700 font-bold">+₹{gCredit.toLocaleString()}</td>
                          <td colSpan={3} className="px-3 py-2 text-[11px] text-slate-600 font-semibold">
                            {group.rows.length} txns · <span className="text-emerald-700">{gMatched} matched</span> · <span className="text-rose-700">{group.rows.length - gMatched} unmatched</span>
                          </td>
                        </tr>
                      )}
                      {group.rows.map((t) => {
                        const m = catMeta(t.category);
                        const matched = isMatched(t);
                        const bulk = Array.isArray(t.reconciled_invoice_ids) && t.reconciled_invoice_ids.length > 1;
                        const rowCls = matched
                          ? "bg-emerald-50 border-l-4 border-l-emerald-500 hover:bg-emerald-100/70"
                          : "bg-rose-50 border-l-4 border-l-rose-500 hover:bg-rose-100/70";
                        return (
                          <tr key={t.id} data-testid={`txn-row-${t.id}`} className={`border-b border-slate-100 ${rowCls}`}>
                            <td className="px-3 py-2 w-8">
                              <input type="checkbox" checked={selectedIds.has(t.id)} onChange={() => toggleSelect(t.id)} data-testid={`txn-select-${t.id}`} className="cursor-pointer h-4 w-4" />
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">{t.txn_date}</td>
                            <td className="px-3 py-2 max-w-xs truncate" title={t.description}>{t.description}</td>
                            <td className="px-3 py-2">{t.party_name || t.reconciled_party || "-"}</td>
                            <td className="px-3 py-2 text-right font-mono text-rose-700">{t.debit ? `₹${t.debit.toLocaleString()}` : ""}</td>
                            <td className="px-3 py-2 text-right font-mono text-emerald-700">{t.credit ? `₹${t.credit.toLocaleString()}` : ""}</td>
                            <td className="px-3 py-2">
                              <select value={t.category || "other"} onChange={(e) => handleUpdateCategory(t.id, e.target.value)} data-testid={`txn-cat-${t.id}`} className={`text-xs px-2 py-1 rounded-sm font-semibold border-0 cursor-pointer ${m.color}`}>
                                {CATEGORIES.map(c => <option key={c.k} value={c.k}>{c.l}</option>)}
                              </select>
                            </td>
                            <td className="px-3 py-2 text-xs">
                              {matched ? (
                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-600 text-white rounded-sm font-bold" title={`${t.settled_via === "auto_party" ? "Auto-settled to party ledger" : t.settled_via === "auto_category" ? "Auto-settled to expense head" : `score: ${t.reconciled_score || "manual"}`}${bulk ? ` · split across ${t.reconciled_invoice_ids.length} bills` : ""}`}>
                                  <LinkIcon size={10} weight="bold" /> {t.reconciled_party || "linked"}{bulk && <span className="ml-1 opacity-80">×{t.reconciled_invoice_ids.length}</span>}
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-rose-600 text-white rounded-sm font-bold">
                                  <Warning size={10} weight="bold" /> Unmatched
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-xs">
                              {matched ? (
                                <button onClick={() => unlinkTxn(t)} data-testid={`txn-unlink-${t.id}`}
                                  className="inline-flex items-center gap-1 px-2 py-1 text-slate-600 hover:text-rose-700 border border-slate-300 hover:border-rose-500 rounded-sm text-[11px] font-semibold">
                                  <LinkBreak size={12} /> Unlink
                                </button>
                              ) : (
                                <button onClick={() => openMapDialog(t)} data-testid={`txn-map-${t.id}`}
                                  className="inline-flex items-center gap-1 px-2 py-1 bg-slate-900 text-white hover:bg-slate-700 rounded-sm text-[11px] font-bold">
                                  <LinkIcon size={12} /> Reconcile
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </Fragment>
                  );
                })
              )}
            </tbody>
            {txns.length > 0 && !loadingTxns && (
              <tfoot>
                <tr className="bg-slate-900 text-white font-bold border-t-2 border-slate-700" data-testid="txn-totals-row">
                  <td colSpan={4} className="px-3 py-2.5 text-xs uppercase tracking-wider">
                    Total · {month || fyDisplay} · {txns.length} entries
                  </td>
                  <td className="px-3 py-2.5 text-right font-mono text-rose-300" data-testid="txn-total-debit">₹{totDebit.toLocaleString()}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-emerald-300" data-testid="txn-total-credit">₹{totCredit.toLocaleString()}</td>
                  <td colSpan={3} className="px-3 py-2.5 text-right whitespace-nowrap">
                    <span className="text-[10px] uppercase text-slate-300 mr-2">Closing (Credit − Debit)</span>
                    <span className={`font-mono text-sm ${totCredit - totDebit >= 0 ? "text-emerald-300" : "text-rose-300"}`} data-testid="txn-closing-balance">₹{(totCredit - totDebit).toLocaleString()}</span>
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
      </>)}

      {tab === "ledger" && (
        <div className="space-y-6" data-testid="ledger-panel">
          {/* Sub-tabs + filters + exports */}
          <div className="flex items-end gap-3 flex-wrap">
            <div className="flex gap-1 bg-slate-100 p-1 rounded-sm flex-wrap">
              {[
                {k:"all",l:"All Ledgers"},
                {k:"sales",l:"Sales"},
                {k:"purchase",l:"Purchase"},
                {k:"expenses",l:"Expenses"},
                {k:"suspense",l:"Suspense"},
              ].map(s => (
                <button key={s.k} onClick={() => { setLedgerKind(s.k); setPartyDetail(null); }} data-testid={`ledger-subtab-${s.k}`}
                  className={`px-4 py-1.5 text-sm font-bold rounded-sm transition-all ${ledgerKind === s.k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"} ${s.k === "suspense" && suspense?.total_entries > 0 ? "text-rose-700" : ""}`}>
                  {s.l}{s.k === "suspense" && suspense?.total_entries > 0 && <span className="ml-1 text-[10px] px-1.5 py-0.5 bg-rose-600 text-white rounded-full">{suspense.total_entries}</span>}
                </button>
              ))}
            </div>
            <div><Label className="text-xs">Financial Year (from sidebar)</Label>
              <div className="h-9 px-3 border border-slate-200 rounded-sm text-sm bg-slate-50 font-bold flex items-center text-slate-700" data-testid="ledger-year-display">{fyDisplay}</div>
            </div>
            <div><Label className="text-xs">Month (optional)</Label><Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40" data-testid="ledger-month" /></div>
            <Button size="sm" variant="outline" onClick={() => setMonth("")}>Clear month</Button>
            <div className="ml-auto flex gap-2">
              <Button size="sm" variant="outline" onClick={() => { const p = new URLSearchParams(); p.set('year', year); if (month) p.set('month', month); downloadFile(`${API}/ledger/${ledgerKind}/excel?${p}`, `${ledgerKind}Ledger_${month||year}.xlsx`); }} data-testid="download-ledger-excel" className="flex items-center gap-1"><DownloadSimple size={14} /> Excel</Button>
              <Button size="sm" variant="outline" onClick={() => { const p = new URLSearchParams(); p.set('year', year); if (month) p.set('month', month); downloadFile(`${API}/ledger/${ledgerKind}/pdf?${p}`, `${ledgerKind}Ledger_${month||year}.pdf`); }} data-testid="download-ledger-pdf" className="flex items-center gap-1"><DownloadSimple size={14} /> PDF</Button>
            </div>
          </div>

          {/* ===== EXPENSES LEDGER — user-defined expense heads (Petrol/Rent/Machinery) ===== */}
          {ledgerKind === "expenses" && (
            <div className="space-y-4" data-testid="expenses-panel">
              <div className="bg-white border border-slate-200 rounded-sm p-4">
                <h3 className="text-sm font-black text-slate-900 mb-3">Create New Expense Head</h3>
                <div className="flex gap-2 flex-wrap items-end">
                  <div className="flex-1 min-w-[200px]">
                    <Label className="text-xs">Name</Label>
                    <Input value={expenseForm.name} data-testid="expense-name-input"
                      onChange={(e) => setExpenseForm({ ...expenseForm, name: e.target.value })}
                      placeholder="e.g. Petrol – Vehicle, Office Rent, Machinery Repair" />
                  </div>
                  <div className="min-w-[220px]" data-testid="expense-under-group-picker">
                    <Label className="text-xs">Under (Ledger Group) *</Label>
                    <GroupPicker groups={acctGroups} value={expenseForm.under_group_id}
                      onChange={(g) => setExpenseForm({ ...expenseForm, under_group_id: g.id, under_group: g.name })}
                      onGroupCreated={(g) => setAcctGroups((gs) => [...gs, g].sort((a, b) => a.name.localeCompare(b.name)))}
                      testid="expense-under-group-picker" />
                  </div>
                  <div className="min-w-[220px]">
                    <Label className="text-xs">Select Ledger (optional)</Label>
                    <select value={expenseForm.linked_ledger_id} data-testid="expense-linked-ledger-select"
                      onChange={(e) => { const l = acctLedgers.find((x) => x.id === e.target.value); setExpenseForm({ ...expenseForm, linked_ledger_id: e.target.value, linked_ledger_name: l ? l.name : "" }); }}
                      className="h-9 w-full px-3 border border-slate-200 rounded-sm text-sm font-semibold block bg-white">
                      <option value="">— None —</option>
                      {acctLedgers.map((l) => <option key={l.id} value={l.id}>{l.name}{l.group_name ? ` (${l.group_name})` : ""}</option>)}
                    </select>
                  </div>
                  <Button onClick={createExpenseLedger} disabled={expenseSaving} data-testid="expense-create-btn"
                    className="bg-slate-900 hover:bg-slate-800 text-white font-bold">
                    {expenseSaving ? "Adding…" : "+ Add"}
                  </Button>
                </div>
              </div>

              <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200 text-[10px] font-bold uppercase text-slate-600">
                    <tr>
                      <th className="px-3 py-2 text-left">Expense Head</th>
                      <th className="px-3 py-2 text-left">Under (Ledger Group)</th>
                      <th className="px-3 py-2 text-right">Spent (FY)</th>
                      <th className="px-3 py-2 text-right">Received Back</th>
                      <th className="px-3 py-2 text-right">Txns</th>
                      <th className="px-3 py-2 text-left">Last Txn</th>
                      <th className="px-3 py-2 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {expenseLedgers.length === 0 ? (
                      <tr><td colSpan={7} className="px-3 py-10 text-center text-slate-400">
                        No expense heads yet. Create one above (Petrol, Rent, Machinery Repair …), then link bank debits to it from the Suspense tab.
                      </td></tr>
                    ) : expenseLedgers.map((e) => (
                      <Fragment key={e.id}>
                      <tr className={`border-b border-slate-100 hover:bg-slate-50 cursor-pointer ${expDetail?.id === e.id ? "bg-blue-50/50" : ""}`} data-testid={`expense-row-${e.id}`}
                        onClick={() => openExpenseDetail(e.id)}>
                        <td className="px-3 py-2 font-black text-slate-900">{expDetail?.id === e.id ? "▾ " : "▸ "}{e.name}</td>
                        <td className="px-3 py-2 text-xs" data-testid={`expense-under-${e.id}`}>{e.under_group ? <span className="inline-block px-2 py-0.5 rounded-sm bg-indigo-50 text-indigo-700 font-bold border border-indigo-200">{e.under_group}</span> : <span className="inline-block px-2 py-0.5 rounded-sm bg-slate-100 text-slate-500 font-bold">{(e.category || "other").replace(/_/g, " ")}</span>}{e.linked_ledger_name && <span className="inline-block ml-1 px-2 py-0.5 rounded-sm bg-teal-50 text-teal-700 font-bold border border-teal-200" data-testid={`expense-linked-${e.id}`}>→ {e.linked_ledger_name}</span>}</td>
                        <td className="px-3 py-2 text-right font-mono text-rose-700 font-bold">₹{(e.spent || 0).toLocaleString()}</td>
                        <td className="px-3 py-2 text-right font-mono text-emerald-700">{e.received_back ? `₹${e.received_back.toLocaleString()}` : ""}</td>
                        <td className="px-3 py-2 text-right text-slate-500">{e.txns || 0}</td>
                        <td className="px-3 py-2 text-slate-500">{e.last_date || "-"}</td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <button onClick={(ev) => { ev.stopPropagation(); startExpenseEdit(e); }} data-testid={`expense-edit-${e.id}`}
                            className="text-blue-700 hover:text-blue-900 text-[11px] font-bold mr-3">Edit</button>
                          <button onClick={(ev) => { ev.stopPropagation(); deleteExpenseLedger(e.id, e.name); }} data-testid={`expense-delete-${e.id}`}
                            className="text-rose-600 hover:text-rose-800 text-[11px] font-bold">Delete</button>
                        </td>
                      </tr>
                      {expEdit?.id === e.id && (
                        <tr>
                          <td colSpan={7} className="px-0 py-0 bg-blue-50/60">
                            <div className="px-4 py-3 flex gap-2 flex-wrap items-end" data-testid={`expense-edit-form-${e.id}`}>
                              <div className="flex-1 min-w-[180px]">
                                <Label className="text-xs">Name</Label>
                                <Input value={expEdit.name} data-testid="expense-edit-name-input"
                                  onChange={(ev) => setExpEdit({ ...expEdit, name: ev.target.value })} />
                              </div>
                              <div className="min-w-[200px]">
                                <Label className="text-xs">Under (Ledger Group)</Label>
                                <GroupPicker groups={acctGroups} value={expEdit.under_group_id}
                                  onChange={(g) => setExpEdit({ ...expEdit, under_group_id: g.id, under_group: g.name })}
                                  onGroupCreated={(g) => setAcctGroups((gs) => [...gs, g].sort((a, b) => a.name.localeCompare(b.name)))}
                                  testid="expense-edit-under-group-picker" />
                              </div>
                              <div className="min-w-[200px]">
                                <Label className="text-xs">Select Ledger (optional)</Label>
                                <select value={expEdit.linked_ledger_id} data-testid="expense-edit-linked-ledger-select"
                                  onChange={(ev) => { const l = acctLedgers.find((x) => x.id === ev.target.value); setExpEdit({ ...expEdit, linked_ledger_id: ev.target.value, linked_ledger_name: l ? l.name : "" }); }}
                                  className="h-9 w-full px-3 border border-slate-200 rounded-sm text-sm font-semibold block bg-white">
                                  <option value="">— None —</option>
                                  {acctLedgers.map((l) => <option key={l.id} value={l.id}>{l.name}{l.group_name ? ` (${l.group_name})` : ""}</option>)}
                                </select>
                              </div>
                              <Button size="sm" onClick={saveExpenseEdit} disabled={expEditSaving} data-testid="expense-edit-save-btn"
                                className="bg-blue-700 hover:bg-blue-800 text-white font-bold">{expEditSaving ? "Saving…" : "Save"}</Button>
                              <Button size="sm" variant="outline" onClick={() => setExpEdit(null)} data-testid="expense-edit-cancel-btn">Cancel</Button>
                            </div>
                          </td>
                        </tr>
                      )}
                      {expDetail?.id === e.id && (
                        <tr>
                          <td colSpan={7} className="px-0 py-0 bg-slate-50/70">
                            <div className="px-4 py-3" data-testid={`expense-detail-${e.id}`}>
                              {(expDetail.data?.transactions || []).length === 0 ? (
                                <p className="text-xs text-slate-400 py-4 text-center">No entries in this expense head yet.</p>
                              ) : (
                                <table className="w-full text-xs bg-white border border-slate-200 rounded-sm">
                                  <thead className="bg-slate-100 text-[10px] font-bold uppercase text-slate-600">
                                    <tr>
                                      <th className="px-3 py-2 text-left">Date</th>
                                      <th className="px-3 py-2 text-left">Description</th>
                                      <th className="px-3 py-2 text-right">Debit</th>
                                      <th className="px-3 py-2 text-right">Credit</th>
                                      <th className="px-3 py-2 text-right">Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {expDetail.data.transactions.map((t) => (
                                      <tr key={t.id} className="border-b border-slate-100" data-testid={`expense-entry-${t.id}`}>
                                        <td className="px-3 py-2 whitespace-nowrap">{t.txn_date}</td>
                                        <td className="px-3 py-2 max-w-md truncate" title={t.description}>{t.description}</td>
                                        <td className="px-3 py-2 text-right font-mono text-rose-700">{t.debit ? `₹${t.debit.toLocaleString()}` : ""}</td>
                                        <td className="px-3 py-2 text-right font-mono text-emerald-700">{t.credit ? `₹${t.credit.toLocaleString()}` : ""}</td>
                                        <td className="px-3 py-2 text-right whitespace-nowrap">
                                          <button onClick={() => openMapDialog({ id: t.id, txn_date: t.txn_date, description: t.description, debit: t.debit || 0, credit: t.credit || 0 })}
                                            data-testid={`expense-entry-reconcile-${t.id}`}
                                            className="inline-flex items-center gap-1 px-2 py-1 mr-2 text-[10px] font-bold text-blue-700 hover:text-white hover:bg-blue-700 border border-blue-400 rounded-sm transition-colors">
                                            <LinkIcon size={10} /> Reconcile
                                          </button>
                                          <button onClick={() => removeExpenseEntry(t.id)}
                                            data-testid={`expense-entry-remove-${t.id}`}
                                            className="px-2 py-1 text-[10px] font-bold text-rose-700 hover:text-white hover:bg-rose-700 border border-rose-400 rounded-sm transition-colors">
                                            ✕ Remove
                                          </button>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ===== SUSPENSE LEDGER — unmatched bank txns grouped by party ===== */}
          {ledgerKind === "suspense" && (
            loadingLedger || !suspense ? (
              <p className="text-slate-500 text-sm">Loading suspense ledger…</p>
            ) : suspense.total_entries === 0 ? (
              <div className="bg-white border border-slate-200 rounded-sm p-10 text-center">
                <p className="text-emerald-700 font-black text-lg">✓ No suspense entries</p>
                <p className="text-slate-500 text-sm mt-2">Every bank transaction has been matched to a sales/purchase bill.</p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="suspense-panel">
                {selTxnIds.size > 0 && (
                  <div className="px-4 py-2 bg-blue-50 border border-blue-200 rounded-sm flex items-center justify-between gap-2" data-testid="bulk-suspense-bar">
                    <p className="text-xs font-bold text-blue-900">{selTxnIds.size} entries selected</p>
                    <div className="flex gap-2">
                      <button data-testid="bulk-suspense-reconcile-btn"
                        onClick={() => openMapDialog({ bulk: [...selTxnIds], txn_date: "", description: `${selTxnIds.size} selected entries`, debit: 0, credit: 0 })}
                        className="px-3 py-1.5 bg-blue-700 hover:bg-blue-800 text-white rounded-sm text-xs font-bold">
                        Reconcile Selected →
                      </button>
                      <button onClick={() => setSelTxnIds(new Set())} data-testid="bulk-suspense-clear"
                        className="px-3 py-1.5 border border-slate-300 text-slate-600 rounded-sm text-xs font-bold hover:bg-slate-100">Clear</button>
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="bg-rose-50 border-2 border-rose-200 rounded-sm p-4">
                    <p className="text-[10px] font-bold uppercase text-rose-700">Unmatched Entries</p>
                    <p className="text-2xl font-black text-rose-900" data-testid="suspense-total">{suspense.total_entries}</p>
                  </div>
                  <div className="bg-white border border-slate-200 rounded-sm p-4">
                    <p className="text-[10px] font-bold uppercase text-slate-500">Parties</p>
                    <p className="text-2xl font-black text-slate-900">{suspense.parties.length}</p>
                    <p className="text-[10px] text-amber-700 font-bold mt-1">{suspense.parties.filter(p => p.is_new_party).length} new</p>
                  </div>
                  <div className="bg-white border border-slate-200 rounded-sm p-4">
                    <p className="text-[10px] font-bold uppercase text-emerald-700">Credits (Money In)</p>
                    <p className="text-2xl font-black text-emerald-700">₹{suspense.total_credit.toLocaleString()}</p>
                  </div>
                  <div className="bg-white border border-slate-200 rounded-sm p-4">
                    <p className="text-[10px] font-bold uppercase text-rose-700">Debits (Money Out)</p>
                    <p className="text-2xl font-black text-rose-700">₹{suspense.total_debit.toLocaleString()}</p>
                  </div>
                </div>

                {suspense.parties.map((p) => (
                  <div key={p.party} className={`bg-white rounded-sm overflow-hidden border-l-4 ${p.is_new_party ? "border-l-amber-500 border border-amber-200" : "border-l-rose-500 border border-rose-200"}`} data-testid={`suspense-party-${p.party.replace(/\s+/g,'-')}`}>
                    <div className={`px-4 py-3 flex items-center justify-between ${p.is_new_party ? "bg-amber-50" : "bg-rose-50"}`}>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] font-black px-2 py-0.5 rounded-sm ${p.is_new_party ? "bg-amber-600 text-white" : "bg-rose-600 text-white"}`}>
                            {p.is_new_party ? "NEW PARTY" : "EXISTING"}
                          </span>
                          <h3 className="text-base font-black text-slate-900">{p.party}</h3>
                        </div>
                        <p className="text-[11px] text-slate-500 mt-0.5">
                          {p.count} entries · {p.first_date} → {p.last_date}
                        </p>
                      </div>
                      <div className="text-right flex items-center gap-3">
                        <div className="flex flex-col gap-1">
                          <button onClick={() => { const q = new URLSearchParams(); q.set('party', p.party); q.set('year', year); if (month) q.set('month', month); downloadFile(`${API}/ledger/suspense-party/excel?${q}`, `Suspense_${p.party.replace(/\s+/g,'_')}.xlsx`); }}
                            data-testid={`suspense-party-excel-${p.party.replace(/\s+/g,'-')}`}
                            className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold text-emerald-700 hover:text-white hover:bg-emerald-700 border border-emerald-400 rounded-sm transition-colors">
                            <DownloadSimple size={10} /> Excel
                          </button>
                          <button onClick={() => { const q = new URLSearchParams(); q.set('party', p.party); q.set('year', year); if (month) q.set('month', month); downloadFile(`${API}/ledger/suspense-party/pdf?${q}`, `Suspense_${p.party.replace(/\s+/g,'_')}.pdf`); }}
                            data-testid={`suspense-party-pdf-${p.party.replace(/\s+/g,'-')}`}
                            className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold text-rose-700 hover:text-white hover:bg-rose-700 border border-rose-400 rounded-sm transition-colors">
                            <DownloadSimple size={10} /> PDF
                          </button>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 font-semibold">Net Movement</p>
                          <p className={`text-lg font-black ${p.net >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                            {p.net >= 0 ? "+" : ""}₹{p.net.toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 border-b border-slate-200 text-[10px] font-bold uppercase text-slate-600">
                          <tr>
                            <th className="px-3 py-2 w-8"></th>
                            <th className="px-3 py-2 text-left">Date</th>
                            <th className="px-3 py-2 text-left">Description</th>
                            <th className="px-3 py-2 text-right">Debit</th>
                            <th className="px-3 py-2 text-right">Credit</th>
                            <th className="px-3 py-2 text-left">Ref</th>
                            <th className="px-3 py-2 text-left">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {p.entries.map((e) => (
                            <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`suspense-entry-${e.id}`}>
                              <td className="px-3 py-2">
                                <input type="checkbox" checked={selTxnIds.has(e.id)}
                                  onChange={() => toggleSelTxn(e.id)}
                                  data-testid={`suspense-select-${e.id}`}
                                  className="w-4 h-4 accent-blue-600 cursor-pointer" />
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-slate-700">{e.txn_date}</td>
                              <td className="px-3 py-2 max-w-md truncate" title={e.description}>{e.description}</td>
                              <td className="px-3 py-2 text-right font-mono text-rose-700">{e.debit ? `₹${e.debit.toLocaleString()}` : ""}</td>
                              <td className="px-3 py-2 text-right font-mono text-emerald-700">{e.credit ? `₹${e.credit.toLocaleString()}` : ""}</td>
                              <td className="px-3 py-2 font-mono text-[11px] text-slate-500">{e.reference_number || "-"}</td>
                              <td className="px-3 py-2">
                                <button onClick={() => openMapDialog(e)} data-testid={`suspense-map-${e.id}`}
                                  className="inline-flex items-center gap-1 px-3 py-1 bg-blue-700 hover:bg-blue-800 text-white rounded-sm text-[11px] font-bold">
                                  <LinkIcon size={11} /> Reconcile
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}

          {/* ===== ALL LEDGERS — vertical list, tap to drill down ===== */}
          {ledgerKind === "all" && (
            loadingLedger || !allParties ? (
              <p className="text-slate-500 text-sm">Loading party ledgers…</p>
            ) : partyDetail ? (
              <div className="space-y-4" data-testid="party-detail-panel">
                <button onClick={() => setPartyDetail(null)} data-testid="party-back-btn" className="text-sm text-blue-700 hover:underline font-semibold flex items-center gap-1">
                  ← Back to all ledgers
                </button>
                <div className="bg-slate-900 text-white rounded-sm p-5">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <p className="text-[10px] uppercase font-bold text-slate-400 tracking-widest">Party Ledger</p>
                      <h2 className="text-2xl font-black mt-1">{partyDetail.party.name}</h2>
                      <p className="text-xs text-slate-400 mt-1">{partyDetail.party.gstin || "No GSTIN"} · {partyDetail.party.state || "-"}</p>
                      <button onClick={() => openReconcileModal(partyDetail.party.id, partyDetail.party.name)}
                        disabled={reconRunning !== null}
                        data-testid="party-detail-reconcile-btn"
                        className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-sm text-xs font-bold disabled:opacity-50">
                        <LinkIcon size={13} weight="bold" />
                        Reconcile — link bills by date + amount
                      </button>
                      {partyReconcileMsg && <p className="text-[11px] text-emerald-300 mt-2 font-semibold" data-testid="party-detail-reconcile-msg">{partyReconcileMsg}</p>}
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-right">
                      <div><p className="text-[10px] uppercase font-bold text-slate-400">Billed</p><p className="text-lg font-black">₹{partyDetail.totals.billed.toLocaleString()}</p></div>
                      <div><p className="text-[10px] uppercase font-bold text-slate-400">Received</p><p className="text-lg font-black text-emerald-400">₹{partyDetail.totals.received.toLocaleString()}</p></div>
                      <div><p className="text-[10px] uppercase font-bold text-slate-400">Closing</p><p className={`text-lg font-black ${partyDetail.totals.closing_balance >= 0 ? "text-amber-400" : "text-blue-400"}`}>₹{partyDetail.totals.closing_balance.toLocaleString()}</p></div>
                    </div>
                  </div>
                </div>
                <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
                  {selTxnIds.size > 0 && (
                    <div className="px-4 py-2 bg-blue-50 border-b border-blue-200 flex items-center justify-between gap-2" data-testid="bulk-events-bar">
                      <p className="text-xs font-bold text-blue-900">{selTxnIds.size} entries selected</p>
                      <div className="flex gap-2">
                        <button data-testid="bulk-events-reconcile-btn"
                          onClick={() => openMapDialog({ bulk: [...selTxnIds], txn_date: "", description: `${selTxnIds.size} selected entries`, debit: 0, credit: 0 })}
                          className="px-3 py-1.5 bg-blue-700 hover:bg-blue-800 text-white rounded-sm text-xs font-bold">
                          Reconcile Selected →
                        </button>
                        <button onClick={() => setSelTxnIds(new Set())} data-testid="bulk-events-clear"
                          className="px-3 py-1.5 border border-slate-300 text-slate-600 rounded-sm text-xs font-bold hover:bg-slate-100">Clear</button>
                      </div>
                    </div>
                  )}
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200 text-[10px] font-bold uppercase text-slate-600 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 w-8"></th>
                        <th className="px-3 py-2 text-left">Date</th>
                        <th className="px-3 py-2 text-left">Purpose</th>
                        <th className="px-3 py-2 text-left">Doc No.</th>
                        <th className="px-3 py-2 text-left">Description</th>
                        <th className="px-3 py-2 text-right">Debit</th>
                        <th className="px-3 py-2 text-right">Credit</th>
                        <th className="px-3 py-2 text-right">Balance</th>
                        <th className="px-3 py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {partyDetail.events.length === 0 ? (
                        <tr><td colSpan={9} className="px-3 py-10 text-center text-slate-400">No entries for this party in {fyLabel(year)}.</td></tr>
                      ) : partyDetail.events.map((e, i) => (
                        <tr key={i} className={`border-b border-slate-100 ${e.in_suspense ? "bg-rose-50 border-l-4 border-l-rose-500" : e.type === "payment_received" ? "bg-emerald-50/40" : e.type === "purchase_bill" ? "bg-amber-50/40" : e.type === "bank_txn" ? "bg-blue-50/40" : ""}`} data-testid={`party-event-${i}`}>
                          <td className="px-3 py-2">
                            {e.bank_txn_id && (
                              <input type="checkbox" checked={selTxnIds.has(e.bank_txn_id)}
                                onChange={() => toggleSelTxn(e.bank_txn_id)}
                                data-testid={`event-select-${i}`}
                                className="w-4 h-4 accent-blue-600 cursor-pointer" />
                            )}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap font-semibold">{e.date}</td>
                          <td className="px-3 py-2 text-xs font-bold text-slate-700">{e.in_suspense ? <span className="text-rose-700" data-testid={`event-suspense-${i}`}>In Suspense (red — reconcile karne par color hat jayega)</span> : e.purpose}</td>
                          <td className="px-3 py-2 font-mono text-xs">{e.doc_no}</td>
                          <td className="px-3 py-2 text-xs text-slate-500 max-w-xs truncate" title={e.description}>{e.description}</td>
                          <td className="px-3 py-2 text-right font-mono text-rose-700">{e.debit ? `₹${e.debit.toLocaleString()}` : ""}</td>
                          <td className="px-3 py-2 text-right font-mono text-emerald-700">{e.credit ? `₹${e.credit.toLocaleString()}` : ""}</td>
                          <td className={`px-3 py-2 text-right font-mono font-bold ${e.balance >= 0 ? "text-amber-700" : "text-blue-700"}`}>₹{e.balance.toLocaleString()}</td>
                          <td className="px-3 py-2 text-right">
                            {e.bank_txn_id && (
                              <button onClick={() => openMapDialog({ id: e.bank_txn_id, txn_date: e.date, description: e.description, debit: e.debit || 0, credit: e.credit || 0 })} data-testid={`event-reconcile-${i}`}
                                title="Move this entry to the correct bill / party / expense"
                                className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold text-blue-700 hover:text-white hover:bg-blue-700 border border-blue-400 hover:border-blue-700 rounded-sm transition-colors">
                                <LinkIcon size={11} /> Reconcile
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="space-y-3" data-testid="all-parties-panel">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div className="bg-white border border-slate-200 rounded-sm p-4">
                    <p className="text-[10px] font-bold uppercase text-slate-500">Total Parties</p>
                    <p className="text-2xl font-black">{allParties.total_parties}</p>
                  </div>
                  <div className="bg-white border border-slate-200 rounded-sm p-4">
                    <p className="text-[10px] font-bold uppercase text-emerald-700">Total Received</p>
                    <p className="text-2xl font-black text-emerald-700">₹{allParties.parties.reduce((s,p)=>s+p.received,0).toLocaleString()}</p>
                  </div>
                  <div className="bg-white border border-slate-200 rounded-sm p-4">
                    <p className="text-[10px] font-bold uppercase text-amber-700">Total Pending</p>
                    <p className="text-2xl font-black text-amber-700">₹{allParties.parties.reduce((s,p)=>s+p.pending,0).toLocaleString()}</p>
                  </div>
                </div>
                <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
                  <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                    <h3 className="text-sm font-black text-slate-900">All Party Ledgers</h3>
                    <p className="text-[11px] text-slate-500">Tap any row to see date-wise entries · Reconcile links bills by date + amount</p>
                  </div>
                  {selParties.size > 0 && (
                    <div className="px-4 py-2 bg-blue-50 border-b border-blue-200 flex items-center justify-between gap-2" data-testid="bulk-parties-bar">
                      <p className="text-xs font-bold text-blue-900">{selParties.size} ledger(s) selected — in sabki entries ek hi ledger me transfer/reconcile hongi</p>
                      <div className="flex gap-2">
                        <button data-testid="bulk-parties-reconcile-btn"
                          onClick={async () => {
                            setReconResult(null); setLedgerSearch("");
                            try { const eh = await axios.get(`${API}/expense-ledgers`); setExpenseHeads(eh.data?.items || []); } catch {}
                            setReconTarget({
                              id: null,
                              name: `${selParties.size} selected ledgers`,
                              multi: [...selParties].map((id) => ({ id, name: (allParties?.parties || []).find((x) => x.id === id)?.name || "" })),
                            });
                          }}
                          className="px-3 py-1.5 bg-blue-700 hover:bg-blue-800 text-white rounded-sm text-xs font-bold">
                          Reconcile Selected →
                        </button>
                        <button onClick={() => setSelParties(new Set())} data-testid="bulk-parties-clear"
                          className="px-3 py-1.5 border border-slate-300 text-slate-600 rounded-sm text-xs font-bold hover:bg-slate-100">Clear</button>
                      </div>
                    </div>
                  )}
                  {partyReconcileMsg && (
                    <p className="px-4 py-2 text-xs font-semibold text-emerald-800 bg-emerald-50 border-b border-emerald-200" data-testid="party-reconcile-msg">
                      <CheckCircle size={12} weight="fill" className="inline mr-1 text-emerald-600" />{partyReconcileMsg}
                    </p>
                  )}
                  {allParties.parties.length === 0 ? (
                    <p className="px-4 py-10 text-center text-slate-400 text-sm">No parties yet — add your first customer to start.</p>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {allParties.parties.map((p) => {
                        const isNew = p.kind === "suspense";
                        const hasAliases = Array.isArray(p.matched_bank_aliases) && p.matched_bank_aliases.length > 0;
                        return (
                        <div key={p.id} onClick={() => !partyLoading && openPartyDetail(p.id)}
                          data-testid={`party-row-${p.id}`}
                          role="button" tabIndex={0}
                          className={`w-full px-4 py-3 flex items-center justify-between hover:bg-slate-100 transition-colors text-left cursor-pointer ${partyLoading ? "opacity-50 pointer-events-none" : ""} ${isNew ? "bg-rose-50 border-l-4 border-l-rose-500" : "bg-white border-l-4 border-l-transparent"}`}>
                          <input type="checkbox" checked={selParties.has(p.id)}
                            onClick={(ev) => ev.stopPropagation()}
                            onChange={(ev) => { ev.stopPropagation(); toggleSelParty(p.id); }}
                            data-testid={`party-select-${p.id}`}
                            className="w-4 h-4 mr-3 shrink-0 accent-blue-600 cursor-pointer" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              {isNew && <span className="text-[10px] font-black px-1.5 py-0.5 rounded-sm bg-rose-600 text-white">SUSPENSE · not in bills</span>}
                              {p.kind === "existing_supplier" && <span className="text-[10px] font-black px-1.5 py-0.5 rounded-sm bg-slate-200 text-slate-700">SUPPLIER</span>}
                              {p.kind === "supplier" && <span className="text-[10px] font-black px-1.5 py-0.5 rounded-sm bg-indigo-100 text-indigo-800">PURCHASE PARTY</span>}
                              <p className="font-black text-slate-900 truncate">{p.name}</p>
                              {hasAliases && (
                                <span className="text-[10px] text-slate-500 italic" title={`Bank aliases: ${p.matched_bank_aliases.join(", ")}`}>
                                  ↔ {p.matched_bank_aliases.slice(0, 2).join(", ")}{p.matched_bank_aliases.length > 2 ? "…" : ""}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-slate-500 mt-0.5">
                              {isNew || p.kind === "existing_supplier"
                                ? `${p.bank_count} bank txns${isNew ? " · not in customers/suppliers yet" : " · matched from purchases"}`
                                : p.kind === "supplier"
                                ? `${p.bills} purchase bills${p.bank_count ? ` · +${p.bank_count} bank txns` : ""}`
                                : `${p.bills} bills · ${p.gstin || "no GSTIN"}${p.bank_count ? ` · +${p.bank_count} bank txns` : ""}`}
                              {p.last_date ? ` · last: ${p.last_date}` : ""}
                            </p>
                          </div>
                          <div className="flex items-center gap-6 text-right">
                            <div>
                              <p className="text-[10px] uppercase font-bold text-slate-500">Billed</p>
                              <p className="text-sm font-black text-slate-900">₹{p.billed.toLocaleString()}</p>
                            </div>
                            <div>
                              <p className="text-[10px] uppercase font-bold text-emerald-700">Received</p>
                              <p className="text-sm font-black text-emerald-700">₹{p.received.toLocaleString()}</p>
                            </div>
                            <div>
                              <p className="text-[10px] uppercase font-bold text-amber-700">Pending</p>
                              <p className="text-base font-black text-amber-700">₹{p.pending.toLocaleString()}</p>
                            </div>
                            {(p.kind === "supplier" || p.kind === "existing_supplier") && (
                              <button onClick={(e) => { e.stopPropagation(); openSupplierEdit(p); }}
                                data-testid={`party-edit-${p.id}`}
                                title="Edit party — Under (Ledger Group) set karein (Sundry Creditors etc.)"
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-slate-300 hover:border-slate-500 text-slate-700 rounded-sm text-[11px] font-bold">
                                <PencilSimple size={12} weight="bold" /> Edit
                              </button>
                            )}
                            <button onClick={(e) => { e.stopPropagation(); openReconcileModal(p.id, p.name); }}
                              disabled={reconRunning !== null}
                              data-testid={`party-reconcile-${p.id}`}
                              title="Link this party's bank entries to their sale/purchase/expense bills by date + amount"
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-sm text-[11px] font-bold disabled:opacity-50">
                              <LinkIcon size={12} weight="bold" />
                              Reconcile
                            </button>
                            <span className="text-slate-400 text-2xl leading-none">›</span>
                          </div>
                        </div>);
                      })}
                    </div>
                  )}
                </div>
              </div>
            )
          )}

          {(ledgerKind === "sales" || ledgerKind === "purchase") && (
          loadingLedger || !ledger ? (
            <p className="text-slate-500 text-sm">Loading ledger…</p>
          ) : (
            <>
              {/* Summary cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="bg-white border border-slate-200 rounded-sm p-4">
                  <p className="text-xs font-bold uppercase text-slate-500">{ledgerKind === "sales" ? "Total Billed" : "Total Purchased"}</p>
                  <p className="text-2xl font-black text-slate-900" data-testid="ledger-billed">₹{ledger.totals.billed.toLocaleString()}</p>
                  <p className="text-xs text-slate-400 mt-1">{ledger.totals.bills} bills</p>
                </div>
                <div className="bg-white border border-slate-200 rounded-sm p-4">
                  <p className="text-xs font-bold uppercase text-slate-500">{ledgerKind === "sales" ? "Received" : "Paid"}</p>
                  <p className="text-2xl font-black text-emerald-700" data-testid="ledger-received">₹{ledger.totals.received.toLocaleString()}</p>
                </div>
                <div className="bg-white border border-slate-200 rounded-sm p-4">
                  <p className="text-xs font-bold uppercase text-slate-500">Pending</p>
                  <p className="text-2xl font-black text-rose-700" data-testid="ledger-pending">₹{ledger.totals.pending.toLocaleString()}</p>
                </div>
                <div className="bg-white border border-slate-200 rounded-sm p-4">
                  <p className="text-xs font-bold uppercase text-slate-500">Qty Moved</p>
                  <p className="text-2xl font-black text-blue-700" data-testid="ledger-qty">{ledger.totals.qty.toLocaleString()}</p>
                </div>
              </div>

              {/* Party-wise table */}
              <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
                  <h3 className="text-sm font-bold text-slate-900">Party-wise — {ledgerKind === "sales" ? "Customers" : "Suppliers"} ({ledger.parties.length})</h3>
                </div>
                <div className="overflow-x-auto sticky-table">
                  <table className="w-full text-sm">
                    <thead className="sticky-thead bg-slate-50 text-xs font-bold uppercase text-slate-600">
                      <tr>
                        <th className="px-3 py-2 text-left">Party</th>
                        <th className="px-3 py-2 text-right">Bills</th>
                        <th className="px-3 py-2 text-right">{ledgerKind === "sales" ? "Billed" : "Purchased"}</th>
                        <th className="px-3 py-2 text-right">{ledgerKind === "sales" ? "Received" : "Paid"}</th>
                        <th className="px-3 py-2 text-right">Pending</th>
                        <th className="px-3 py-2 text-right">Qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ledger.parties.length === 0 ? (
                        <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400">No {ledgerKind} bills in this period</td></tr>
                      ) : ledger.parties.map((r, i) => (
                        <tr key={i} data-testid={`ledger-party-${i}`} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-3 py-2 font-semibold text-slate-900">{r.party}</td>
                          <td className="px-3 py-2 text-right font-mono">{r.bills}</td>
                          <td className="px-3 py-2 text-right font-mono">₹{r.billed.toLocaleString()}</td>
                          <td className="px-3 py-2 text-right font-mono text-emerald-700">₹{r.received.toLocaleString()}</td>
                          <td className="px-3 py-2 text-right font-mono font-bold text-rose-700">₹{r.pending.toLocaleString()}</td>
                          <td className="px-3 py-2 text-right font-mono">{r.qty.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Month-wise table */}
              <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
                  <h3 className="text-sm font-bold text-slate-900">Month-wise Breakdown</h3>
                </div>
                <div className="overflow-x-auto sticky-table">
                  <table className="w-full text-sm">
                    <thead className="sticky-thead bg-slate-50 text-xs font-bold uppercase text-slate-600">
                      <tr>
                        <th className="px-3 py-2 text-left">Month</th>
                        <th className="px-3 py-2 text-right">Bills</th>
                        <th className="px-3 py-2 text-right">{ledgerKind === "sales" ? "Billed" : "Purchased"}</th>
                        <th className="px-3 py-2 text-right">{ledgerKind === "sales" ? "Received" : "Paid"}</th>
                        <th className="px-3 py-2 text-right">Pending</th>
                        <th className="px-3 py-2 text-right">Qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ledger.monthly.length === 0 ? (
                        <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400">No data</td></tr>
                      ) : ledger.monthly.map((m, i) => (
                        <tr key={i} data-testid={`ledger-month-${i}`} className="border-b border-slate-100">
                          <td className="px-3 py-2 font-semibold">{m.month}</td>
                          <td className="px-3 py-2 text-right font-mono">{m.bills}</td>
                          <td className="px-3 py-2 text-right font-mono">₹{m.billed.toLocaleString()}</td>
                          <td className="px-3 py-2 text-right font-mono text-emerald-700">₹{m.received.toLocaleString()}</td>
                          <td className="px-3 py-2 text-right font-mono text-rose-700">₹{m.pending.toLocaleString()}</td>
                          <td className="px-3 py-2 text-right font-mono">{m.qty.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ))}
        </div>
      )}

      {tab === "outstanding" && (
        <div className="space-y-6" data-testid="outstanding-panel">
          {!outstanding ? (
            <p className="text-slate-500 text-sm">Loading outstanding items…</p>
          ) : (
            <>
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="outline" onClick={() => downloadFile(`${API}/reconciliation/outstanding/excel`, "Outstanding.xlsx")} data-testid="download-outstanding-excel" className="flex items-center gap-1"><DownloadSimple size={14} /> Excel</Button>
                <Button size="sm" variant="outline" onClick={() => downloadFile(`${API}/reconciliation/outstanding/pdf`, "Outstanding.pdf")} data-testid="download-outstanding-pdf" className="flex items-center gap-1"><DownloadSimple size={14} /> PDF</Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="bg-white border border-slate-200 rounded-sm p-4">
                  <p className="text-xs font-bold uppercase text-slate-500">Receivables (sales unpaid)</p>
                  <p className="text-2xl font-black text-rose-700">₹{outstanding.receivable_total.toLocaleString()}</p>
                  <p className="text-xs text-slate-400 mt-1">{outstanding.receivables.length} invoices</p>
                </div>
                <div className="bg-white border border-slate-200 rounded-sm p-4">
                  <p className="text-xs font-bold uppercase text-slate-500">Payables (purchases unpaid)</p>
                  <p className="text-2xl font-black text-amber-700">₹{outstanding.payable_total.toLocaleString()}</p>
                  <p className="text-xs text-slate-400 mt-1">{outstanding.payables.length} purchases</p>
                </div>
              </div>

              <div className="bg-white border border-slate-200 rounded-sm">
                <div className="p-3 border-b border-slate-200 flex items-center gap-2">
                  <TrendUp size={18} className="text-rose-600" />
                  <h3 className="font-bold text-slate-900">Receivables — whose payments have NOT been received</h3>
                </div>
                <div className="overflow-x-auto sticky-table">
                  <table className="w-full text-sm">
                    <thead className="sticky-thead bg-slate-50 text-xs font-bold uppercase text-slate-600">
                      <tr>
                        <th className="px-3 py-2 text-left">Invoice</th>
                        <th className="px-3 py-2 text-left">Date</th>
                        <th className="px-3 py-2 text-left">Customer</th>
                        <th className="px-3 py-2 text-right">Total</th>
                        <th className="px-3 py-2 text-right">Paid</th>
                        <th className="px-3 py-2 text-right">Balance</th>
                        <th className="px-3 py-2 text-left">Due</th>
                      </tr>
                    </thead>
                    <tbody>
                      {outstanding.receivables.length === 0 ? (
                        <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-400">All clear — every sale has been received.</td></tr>
                      ) : outstanding.receivables.map(r => (
                        <tr key={r.id} data-testid={`receivable-${r.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-3 py-2 font-semibold">{r.invoice_no}</td>
                          <td className="px-3 py-2">{r.date}</td>
                          <td className="px-3 py-2">{r.party}</td>
                          <td className="px-3 py-2 text-right font-mono">₹{r.total.toLocaleString()}</td>
                          <td className="px-3 py-2 text-right font-mono text-emerald-700">₹{r.paid.toLocaleString()}</td>
                          <td className="px-3 py-2 text-right font-mono text-rose-700 font-bold">₹{r.balance.toLocaleString()}</td>
                          <td className="px-3 py-2 text-xs">{r.due_date || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="bg-white border border-slate-200 rounded-sm">
                <div className="p-3 border-b border-slate-200 flex items-center gap-2">
                  <TrendDown size={18} className="text-amber-600" />
                  <h3 className="font-bold text-slate-900">Payables — whose bills you haven't paid</h3>
                </div>
                <div className="overflow-x-auto sticky-table">
                  <table className="w-full text-sm">
                    <thead className="sticky-thead bg-slate-50 text-xs font-bold uppercase text-slate-600">
                      <tr><th className="px-3 py-2 text-left">Purchase</th><th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-left">Vendor</th><th className="px-3 py-2 text-right">Amount</th></tr>
                    </thead>
                    <tbody>
                      {outstanding.payables.length === 0 ? (
                        <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-400">All purchases paid.</td></tr>
                      ) : outstanding.payables.map(p => (
                        <tr key={p.id} data-testid={`payable-${p.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-3 py-2 font-semibold">{p.purchase_no}</td>
                          <td className="px-3 py-2">{p.date}</td>
                          <td className="px-3 py-2">{p.party}</td>
                          <td className="px-3 py-2 text-right font-mono text-amber-700 font-bold">₹{p.total.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {tab === "financials" && (
        <div className="space-y-6" data-testid="financials-panel">
          <div className="flex items-end gap-3 mb-2 flex-wrap">
            <div><Label className="text-xs">Financial Year (from sidebar)</Label>
              <div className="h-9 px-3 border border-slate-200 rounded-sm text-sm bg-slate-50 font-bold flex items-center text-slate-700" data-testid="financials-year-display">{fyDisplay}</div>
            </div>
            <div><Label className="text-xs">Month (optional)</Label><Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40" /></div>
            <Button size="sm" variant="outline" onClick={() => setMonth("")}>Clear month</Button>
            <div className="ml-auto flex gap-2">
              <span className="text-xs text-slate-500 self-center mr-1">P&amp;L:</span>
              <Button size="sm" variant="outline" onClick={() => { const p = new URLSearchParams(); p.set('year', year); if (month) p.set('month', month); downloadFile(`${API}/financials/pnl/excel?${p}`, `PnL_${month||year}.xlsx`); }} data-testid="download-pnl-excel" className="flex items-center gap-1"><DownloadSimple size={14} /> Excel</Button>
              <Button size="sm" variant="outline" onClick={() => { const p = new URLSearchParams(); p.set('year', year); if (month) p.set('month', month); downloadFile(`${API}/financials/pnl/pdf?${p}`, `PnL_${month||year}.pdf`); }} data-testid="download-pnl-pdf" className="flex items-center gap-1"><DownloadSimple size={14} /> PDF</Button>
              <span className="text-xs text-slate-500 self-center ml-2 mr-1">BS:</span>
              <Button size="sm" variant="outline" onClick={() => downloadFile(`${API}/financials/balance-sheet/excel`, "BalanceSheet.xlsx")} data-testid="download-bs-excel" className="flex items-center gap-1"><DownloadSimple size={14} /> Excel</Button>
              <Button size="sm" variant="outline" onClick={() => downloadFile(`${API}/financials/balance-sheet/pdf`, "BalanceSheet.pdf")} data-testid="download-bs-pdf" className="flex items-center gap-1"><DownloadSimple size={14} /> PDF</Button>
            </div>
          </div>

          {/* FY vs FY Comparison */}
          {!month && pnl && prevPnl && (
            <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="fy-compare-card">
              <div className="flex items-center gap-2 mb-4 pb-3 border-b border-slate-200">
                <TrendUp size={22} weight="duotone" className="text-indigo-600" />
                <h3 className="text-xl font-bold text-slate-900">FY Comparison — {fyLabel(year)} vs {fyLabel(year - 1)}</h3>
              </div>
              <div className="overflow-x-auto sticky-table">
                <table className="w-full text-sm">
                  <thead className="sticky-thead bg-slate-50">
                    <tr className="text-slate-600 font-bold uppercase text-xs">
                      <th className="px-3 py-2 text-left">Particulars</th>
                      <th className="px-3 py-2 text-right">{fyLabel(year)}</th>
                      <th className="px-3 py-2 text-right">{fyLabel(year - 1)}</th>
                      <th className="px-3 py-2 text-right">Growth</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { l: "Revenue", a: pnl.revenue, b: prevPnl.revenue, goodUp: true },
                      { l: "Purchases", a: pnl.purchases, b: prevPnl.purchases, goodUp: false },
                      { l: "Gross Profit", a: pnl.gross_profit, b: prevPnl.gross_profit, goodUp: true },
                      { l: "Other Expenses", a: pnl.other_expenses_total, b: prevPnl.other_expenses_total, goodUp: false },
                      { l: "Bank Charges", a: pnl.bank_charges, b: prevPnl.bank_charges, goodUp: false },
                      { l: "NET PROFIT", a: pnl.net_profit, b: prevPnl.net_profit, goodUp: true, bold: true },
                    ].map((r, i) => {
                      const growth = r.b ? ((r.a - r.b) / Math.abs(r.b)) * 100 : (r.a ? 100 : 0);
                      const up = growth >= 0;
                      const good = r.goodUp ? up : !up;
                      return (
                        <tr key={i} data-testid={`fy-compare-row-${i}`} className={`border-b border-slate-100 ${r.bold ? "bg-slate-900 text-white font-bold" : ""}`}>
                          <td className="px-3 py-2 font-semibold">{r.l}</td>
                          <td className="px-3 py-2 text-right font-mono">₹{(r.a || 0).toLocaleString()}</td>
                          <td className="px-3 py-2 text-right font-mono">₹{(r.b || 0).toLocaleString()}</td>
                          <td className={`px-3 py-2 text-right font-bold ${r.bold ? (good ? "text-emerald-300" : "text-rose-300") : (good ? "text-emerald-700" : "text-rose-700")}`}>
                            {up ? "▲" : "▼"} {Math.abs(growth).toFixed(1)}%
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Profit & Loss */}
          <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="pnl-card">
            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-slate-200">
              <Scales size={22} weight="duotone" className="text-blue-600" />
              <h3 className="text-xl font-bold text-slate-900">Profit &amp; Loss — {month || fyLabel(year)}</h3>
            </div>
            {pnl && (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between py-1.5"><span className="text-slate-600">Revenue (from invoices)</span><span className="font-bold text-emerald-700">₹{pnl.revenue.toLocaleString()}</span></div>
                <div className="flex justify-between py-1.5"><span className="text-slate-600">(–) Purchases</span><span className="font-bold text-rose-700">₹{pnl.purchases.toLocaleString()}</span></div>
                <div className="flex justify-between py-1.5 bg-slate-50 px-2 rounded-sm"><span className="font-bold text-slate-900">Gross Profit</span><span className="font-bold text-slate-900">₹{pnl.gross_profit.toLocaleString()}</span></div>
                {pnl.other_expenses.length > 0 && (
                  <div className="pl-4 border-l-2 border-slate-200 ml-2 my-2 space-y-1">
                    {pnl.other_expenses.map(e => (
                      <div key={e.category} className="flex justify-between text-xs"><span className="text-slate-500">(–) {e.category}</span><span className="text-rose-700">₹{e.amount.toLocaleString()}</span></div>
                    ))}
                  </div>
                )}
                <div className="flex justify-between py-1.5"><span className="text-slate-600">(–) Other Expenses</span><span className="font-bold text-rose-700">₹{pnl.other_expenses_total.toLocaleString()}</span></div>
                <div className="flex justify-between py-1.5"><span className="text-slate-600">(–) Bank Charges</span><span className="font-bold text-rose-700">₹{pnl.bank_charges.toLocaleString()}</span></div>
                <div className="flex justify-between py-3 mt-2 bg-slate-900 text-white px-3 rounded-sm"><span className="font-bold text-lg">NET PROFIT</span><span className="font-black text-2xl" data-testid="net-profit">₹{pnl.net_profit.toLocaleString()}</span></div>
              </div>
            )}
          </div>

          {/* Bill-wise P&L detail */}
          <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="pnl-detail-card">
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-200">
              <h3 className="text-lg font-bold text-slate-900">Bill-wise Breakdown ({month || fyLabel(year)})</h3>
              <button onClick={() => setShowDetail(v => !v)} data-testid="toggle-detail" className="text-xs text-blue-600 font-semibold hover:underline">{showDetail ? "Hide" : "Show"}</button>
            </div>
            {showDetail && pnlDetail && (
              <div className="space-y-6">
                {/* Sales */}
                <div>
                  <p className="text-xs font-bold uppercase text-emerald-700 mb-2">Sales Invoices ({pnlDetail.sales.length}) — Total ₹{pnlDetail.sales_total.toLocaleString()}</p>
                  <div className="overflow-x-auto sticky-table">
                    <table className="w-full text-xs">
                      <thead className="sticky-thead bg-emerald-50"><tr className="text-emerald-900 font-bold uppercase">
                        <th className="px-2 py-1.5 text-left">Invoice</th><th className="px-2 py-1.5 text-left">Date</th><th className="px-2 py-1.5 text-left">Customer</th>
                        <th className="px-2 py-1.5 text-right">Subtotal</th><th className="px-2 py-1.5 text-right">GST</th><th className="px-2 py-1.5 text-right">Total</th>
                        <th className="px-2 py-1.5 text-right">Paid</th><th className="px-2 py-1.5 text-left">Status</th>
                      </tr></thead>
                      <tbody>
                        {pnlDetail.sales.length === 0 ? <tr><td colSpan={8} className="px-2 py-3 text-center text-slate-400">No sales in this period</td></tr> : pnlDetail.sales.map((s, i) => (
                          <tr key={i} data-testid={`pnl-sale-${i}`} className="border-b border-slate-100">
                            <td className="px-2 py-1.5 font-semibold">{s.invoice_no}</td>
                            <td className="px-2 py-1.5">{s.date}</td>
                            <td className="px-2 py-1.5">{s.party}</td>
                            <td className="px-2 py-1.5 text-right font-mono">₹{s.subtotal.toLocaleString()}</td>
                            <td className="px-2 py-1.5 text-right font-mono">₹{s.gst.toLocaleString()}</td>
                            <td className="px-2 py-1.5 text-right font-mono font-bold">₹{s.total.toLocaleString()}</td>
                            <td className="px-2 py-1.5 text-right font-mono text-emerald-700">₹{s.paid.toLocaleString()}</td>
                            <td className="px-2 py-1.5"><span className={`text-xs px-1.5 py-0.5 rounded-sm font-semibold ${s.status === 'paid' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>{s.status}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Purchases */}
                <div>
                  <p className="text-xs font-bold uppercase text-rose-700 mb-2">Purchase Bills ({pnlDetail.purchases.length}) — Total ₹{pnlDetail.purchases_total.toLocaleString()}</p>
                  <div className="overflow-x-auto sticky-table">
                    <table className="w-full text-xs">
                      <thead className="sticky-thead bg-rose-50"><tr className="text-rose-900 font-bold uppercase">
                        <th className="px-2 py-1.5 text-left">Bill #</th><th className="px-2 py-1.5 text-left">Date</th><th className="px-2 py-1.5 text-left">Vendor</th>
                        <th className="px-2 py-1.5 text-right">Subtotal</th><th className="px-2 py-1.5 text-right">GST</th><th className="px-2 py-1.5 text-right">Total</th>
                      </tr></thead>
                      <tbody>
                        {pnlDetail.purchases.length === 0 ? <tr><td colSpan={6} className="px-2 py-3 text-center text-slate-400">No purchases in this period</td></tr> : pnlDetail.purchases.map((p, i) => (
                          <tr key={i} data-testid={`pnl-purchase-${i}`} className="border-b border-slate-100">
                            <td className="px-2 py-1.5 font-semibold">{p.purchase_no}</td>
                            <td className="px-2 py-1.5">{p.date}</td>
                            <td className="px-2 py-1.5">{p.party}</td>
                            <td className="px-2 py-1.5 text-right font-mono">₹{p.subtotal.toLocaleString()}</td>
                            <td className="px-2 py-1.5 text-right font-mono">₹{p.gst.toLocaleString()}</td>
                            <td className="px-2 py-1.5 text-right font-mono font-bold">₹{p.total.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Bank Charges detail */}
                {pnlDetail.bank_charges.length > 0 && (
                  <div>
                    <p className="text-xs font-bold uppercase text-orange-700 mb-2">Bank Charges ({pnlDetail.bank_charges.length}) — Total ₹{pnlDetail.bank_charges_total.toLocaleString()}</p>
                    <div className="overflow-x-auto sticky-table">
                      <table className="w-full text-xs">
                        <thead className="sticky-thead bg-orange-50"><tr className="text-orange-900 font-bold uppercase"><th className="px-2 py-1.5 text-left">Date</th><th className="px-2 py-1.5 text-left">Description</th><th className="px-2 py-1.5 text-right">Amount</th></tr></thead>
                        <tbody>
                          {pnlDetail.bank_charges.map((b, i) => (
                            <tr key={i} className="border-b border-slate-100">
                              <td className="px-2 py-1.5">{b.date}</td>
                              <td className="px-2 py-1.5 truncate max-w-sm">{b.description}</td>
                              <td className="px-2 py-1.5 text-right font-mono">₹{b.amount.toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Other Expenses manual entry */}
          <div className="bg-white border border-slate-200 rounded-sm p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-3">Manual Expenses (rent, salaries, utilities, etc.)</h3>
            <form onSubmit={handleAddExpense} className="grid grid-cols-1 sm:grid-cols-5 gap-2 mb-4 items-end">
              <div><Label className="text-xs">Date</Label><Input type="date" required value={expForm.expense_date} onChange={(e) => setExpForm({...expForm, expense_date: e.target.value})} data-testid="exp-date" /></div>
              <div><Label className="text-xs">Category</Label><Input required placeholder="Rent / Salary / Utility" value={expForm.category} onChange={(e) => setExpForm({...expForm, category: e.target.value})} data-testid="exp-cat" /></div>
              <div><Label className="text-xs">Amount (₹)</Label><Input type="number" step="0.01" required value={expForm.amount} onChange={(e) => setExpForm({...expForm, amount: e.target.value})} data-testid="exp-amt" /></div>
              <div><Label className="text-xs">Description</Label><Input value={expForm.description} onChange={(e) => setExpForm({...expForm, description: e.target.value})} data-testid="exp-desc" /></div>
              <Button type="submit" data-testid="add-exp-btn" className="bg-slate-900 hover:bg-slate-800 text-white flex items-center gap-1"><Plus size={14} /> Add</Button>
            </form>
            {otherExpenses.length > 0 && (
              <div className="overflow-x-auto sticky-table">
                <table className="w-full text-sm">
                  <thead className="sticky-thead bg-slate-50 text-xs font-bold uppercase text-slate-600">
                    <tr><th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-left">Category</th><th className="px-3 py-2 text-right">Amount</th><th className="px-3 py-2 text-left">Description</th><th className="px-3 py-2"></th></tr>
                  </thead>
                  <tbody>
                    {otherExpenses.map(e => (
                      <tr key={e.id} className="border-b border-slate-100">
                        <td className="px-3 py-2">{e.expense_date}</td>
                        <td className="px-3 py-2 font-semibold">{e.category}</td>
                        <td className="px-3 py-2 text-right font-mono text-rose-700">₹{Number(e.amount).toLocaleString()}</td>
                        <td className="px-3 py-2 text-slate-500">{e.description || "-"}</td>
                        <td className="px-3 py-2"><button onClick={() => handleDeleteExpense(e.id)} className="text-rose-500 hover:bg-rose-50 rounded-sm p-1" data-testid={`del-exp-${e.id}`}><Trash size={14} /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Balance Sheet */}
          <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="bs-card">
            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-slate-200">
              <Scales size={22} weight="duotone" className="text-emerald-600" />
              <h3 className="text-xl font-bold text-slate-900">Balance Sheet as on {bs?.as_of}</h3>
              {bs?.balanced ? <span className="ml-auto text-xs bg-emerald-100 text-emerald-800 px-2 py-1 rounded-sm font-bold">BALANCED ✓</span> : <span className="ml-auto text-xs bg-rose-100 text-rose-800 px-2 py-1 rounded-sm font-bold">UNBALANCED</span>}
            </div>
            {bs && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <p className="text-xs font-bold uppercase text-slate-500 mb-2">Assets</p>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between py-1.5"><span>Bank Balance</span><span className="font-mono">₹{bs.assets.bank.toLocaleString()}</span></div>
                    <div className="flex justify-between py-1.5"><span>Receivables</span><span className="font-mono">₹{bs.assets.receivables.toLocaleString()}</span></div>
                    <div className="flex justify-between py-2 mt-1 bg-emerald-50 px-2 rounded-sm font-bold"><span>Total Assets</span><span className="font-mono" data-testid="total-assets">₹{bs.assets.total.toLocaleString()}</span></div>
                  </div>
                </div>
                <div>
                  <p className="text-xs font-bold uppercase text-slate-500 mb-2">Liabilities + Equity</p>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between py-1.5"><span>Payables</span><span className="font-mono">₹{bs.liabilities.payables.toLocaleString()}</span></div>
                    <div className="flex justify-between py-1.5"><span>Retained Earnings (YTD)</span><span className="font-mono">₹{bs.equity.retained_earnings_ytd.toLocaleString()}</span></div>
                    <div className="flex justify-between py-2 mt-1 bg-blue-50 px-2 rounded-sm font-bold"><span>Total L + E</span><span className="font-mono">₹{(bs.liabilities.total + bs.equity.total).toLocaleString()}</span></div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Bill-wise Balance Sheet detail */}
          <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="bs-detail-card">
            <div className="mb-4 pb-3 border-b border-slate-200">
              <h3 className="text-lg font-bold text-slate-900">Balance Sheet — Bill-wise Detail</h3>
            </div>
            {bsDetail && (
              <div className="space-y-6">
                {/* Bank accounts */}
                {bsDetail.bank_accounts.length > 0 && (
                  <div>
                    <p className="text-xs font-bold uppercase text-blue-700 mb-2">Bank Balances</p>
                    <div className="overflow-x-auto sticky-table">
                      <table className="w-full text-xs">
                        <thead className="sticky-thead bg-blue-50"><tr className="text-blue-900 font-bold uppercase"><th className="px-2 py-1.5 text-left">Bank</th><th className="px-2 py-1.5 text-left">Account</th><th className="px-2 py-1.5 text-left">As of</th><th className="px-2 py-1.5 text-right">Closing Balance</th></tr></thead>
                        <tbody>
                          {bsDetail.bank_accounts.map((a, i) => (
                            <tr key={i} className="border-b border-slate-100">
                              <td className="px-2 py-1.5 font-semibold">{a.bank_name || "-"}</td>
                              <td className="px-2 py-1.5">{a.account_number || "-"}</td>
                              <td className="px-2 py-1.5">{a.period_end || "-"}</td>
                              <td className="px-2 py-1.5 text-right font-mono font-bold">₹{a.closing_balance.toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                {/* Receivables */}
                <div>
                  <p className="text-xs font-bold uppercase text-emerald-700 mb-2">Receivables — Every unpaid sale ({bsDetail.receivables.length}) — Total ₹{bsDetail.receivables_total.toLocaleString()}</p>
                  <div className="overflow-x-auto sticky-table">
                    <table className="w-full text-xs">
                      <thead className="sticky-thead bg-emerald-50"><tr className="text-emerald-900 font-bold uppercase">
                        <th className="px-2 py-1.5 text-left">Invoice</th><th className="px-2 py-1.5 text-left">Date</th><th className="px-2 py-1.5 text-left">Customer</th>
                        <th className="px-2 py-1.5 text-right">Total</th><th className="px-2 py-1.5 text-right">Paid</th><th className="px-2 py-1.5 text-right">Balance</th><th className="px-2 py-1.5 text-left">Due</th>
                      </tr></thead>
                      <tbody>
                        {bsDetail.receivables.length === 0 ? <tr><td colSpan={7} className="px-2 py-3 text-center text-slate-400">No outstanding receivables.</td></tr> : bsDetail.receivables.map((r, i) => (
                          <tr key={i} data-testid={`bs-recv-${i}`} className="border-b border-slate-100">
                            <td className="px-2 py-1.5 font-semibold">{r.invoice_no}</td>
                            <td className="px-2 py-1.5">{r.date}</td>
                            <td className="px-2 py-1.5">{r.party}</td>
                            <td className="px-2 py-1.5 text-right font-mono">₹{r.total.toLocaleString()}</td>
                            <td className="px-2 py-1.5 text-right font-mono text-emerald-700">₹{r.paid.toLocaleString()}</td>
                            <td className="px-2 py-1.5 text-right font-mono font-bold text-rose-700">₹{r.balance.toLocaleString()}</td>
                            <td className="px-2 py-1.5">{r.due_date || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                {/* Payables */}
                <div>
                  <p className="text-xs font-bold uppercase text-amber-700 mb-2">Payables — Every unpaid purchase ({bsDetail.payables.length}) — Total ₹{bsDetail.payables_total.toLocaleString()}</p>
                  <div className="overflow-x-auto sticky-table">
                    <table className="w-full text-xs">
                      <thead className="sticky-thead bg-amber-50"><tr className="text-amber-900 font-bold uppercase">
                        <th className="px-2 py-1.5 text-left">Bill #</th><th className="px-2 py-1.5 text-left">Date</th><th className="px-2 py-1.5 text-left">Vendor</th><th className="px-2 py-1.5 text-right">Amount</th>
                      </tr></thead>
                      <tbody>
                        {bsDetail.payables.length === 0 ? <tr><td colSpan={4} className="px-2 py-3 text-center text-slate-400">All purchases paid.</td></tr> : bsDetail.payables.map((p, i) => (
                          <tr key={i} data-testid={`bs-pay-${i}`} className="border-b border-slate-100">
                            <td className="px-2 py-1.5 font-semibold">{p.purchase_no}</td>
                            <td className="px-2 py-1.5">{p.date}</td>
                            <td className="px-2 py-1.5">{p.party}</td>
                            <td className="px-2 py-1.5 text-right font-mono font-bold text-amber-700">₹{p.total.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <p className="text-xs text-slate-400 mt-4 italic">Phases A (upload &amp; AI-extract) + B (reconcile) + C (P&amp;L, Balance Sheet) are live.</p>

      {/* Manual mapping dialog — appears when user clicks "Map" on an unmatched row */}
      {mapTxn && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="map-dialog"
          onClick={(e) => { if (e.target === e.currentTarget) setMapTxn(null); }}>
          <div className="bg-white rounded-sm shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-900 text-white">
              <div>
                <h3 className="text-lg font-black flex items-center gap-2"><LinkIcon size={18} weight="bold" /> Reconcile — Choose Ledger</h3>
                <p className="text-xs text-slate-300 mt-1">
                  {mapTxn.txn_date} · {mapTxn.description?.slice(0, 80)} · <span className="text-emerald-300 font-bold">{mapTxn.credit ? `+₹${mapTxn.credit.toLocaleString()}` : ""}</span><span className="text-rose-300 font-bold">{mapTxn.debit ? `-₹${mapTxn.debit.toLocaleString()}` : ""}</span>
                </p>
              </div>
              <button onClick={() => setMapTxn(null)} className="text-slate-400 hover:text-white text-2xl leading-none" data-testid="map-close-btn">×</button>
            </div>
            <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2 flex-wrap">
              <div className="flex gap-1 bg-slate-100 p-1 rounded-sm">
                {[{k:"party",l:"Party Ledgers"},{k:"expense",l:"Expense Ledgers"}].map((k) => (
                  <button key={k.k} data-testid={`map-kind-${k.k}`}
                    onClick={() => { setMapKind(k.k); setMapQuery(""); searchMapCandidates(k.k, ""); }}
                    className={`px-3 py-1 text-xs font-bold rounded-sm ${mapKind === k.k ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}>
                    {k.l}
                  </button>
                ))}
              </div>
              <button onClick={() => applyLedger("suspense", null)} disabled={mapSaving} data-testid="map-move-suspense"
                className="px-3 py-1.5 text-xs font-bold rounded-sm bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-50">
                ! Move to Suspense
              </button>
              <div className="relative flex-1 min-w-[200px]">
                <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input value={mapQuery} onChange={(e) => { setMapQuery(e.target.value); searchMapCandidates(mapKind, e.target.value); }}
                  placeholder="Search ledger name…"
                  data-testid="map-search-input"
                  className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
            <p className="px-5 py-2 text-[11px] text-slate-500 border-b border-slate-100 bg-slate-50">
              Selecting a ledger will automatically settle this entry against that party's bills by <b>date + amount</b> — any remaining amount stays on-account.
            </p>
            <div className="flex-1 overflow-y-auto">
              {mapCandidates.length === 0 ? (
                <div className="px-5 py-10 text-center text-slate-400 text-sm">No matching ledgers found — try a different search term.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200 text-xs font-bold uppercase text-slate-600">
                    <tr>
                      <th className="px-4 py-2 text-left">Ledger</th>
                      <th className="px-4 py-2 text-left">{mapKind === "expense" ? "Category" : "Type"}</th>
                      <th className="px-4 py-2 text-right">{mapKind === "expense" ? "Spent so far" : "Billed"}</th>
                      <th className="px-4 py-2 text-right">{mapKind === "expense" ? "Txns" : "Pending"}</th>
                      <th className="px-4 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {mapCandidates.map((doc) => {
                      if (mapKind === "expense") {
                        return (
                          <tr key={doc.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`map-cand-${doc.id}`}>
                            <td className="px-4 py-2 font-black text-slate-900">{doc.name}</td>
                            <td className="px-4 py-2 text-xs"><span className="inline-block px-2 py-0.5 rounded-sm bg-slate-100 text-slate-700 font-bold">{(doc.category || "other").replace(/_/g, " ")}</span></td>
                            <td className="px-4 py-2 text-right font-mono text-rose-700 font-bold">₹{(doc.spent || 0).toLocaleString()}</td>
                            <td className="px-4 py-2 text-right text-slate-500">{doc.txns || 0}</td>
                            <td className="px-4 py-2 text-right">
                              <button onClick={() => applyLedger("expense", doc)} disabled={mapSaving} data-testid={`map-apply-${doc.id}`}
                                className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-sm disabled:opacity-50">
                                {mapSaving ? "Linking…" : "Reconcile"}
                              </button>
                            </td>
                          </tr>
                        );
                      }
                      return (
                        <tr key={doc.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`map-cand-${doc.id}`}>
                          <td className="px-4 py-2 font-black text-slate-900">{doc.name}</td>
                          <td className="px-4 py-2 text-xs">
                            <span className={`inline-block px-2 py-0.5 rounded-sm font-bold ${doc.kind === "suspense" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700"}`}>
                              {doc.kind === "suspense" ? "NEW PARTY" : (doc.kind || "party").toUpperCase()}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-right font-mono text-slate-700">₹{(doc.billed || 0).toLocaleString()}</td>
                          <td className="px-4 py-2 text-right font-mono font-bold text-amber-700">₹{(doc.pending || 0).toLocaleString()}</td>
                          <td className="px-4 py-2 text-right">
                            <button onClick={() => applyLedger("party", doc)} disabled={mapSaving} data-testid={`map-apply-${doc.id}`}
                              className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-sm disabled:opacity-50">
                              {mapSaving ? "Linking…" : "Reconcile"}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Supplier / purchase-party edit dialog */}
      {supEdit && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="supplier-edit-modal">
          <div className="bg-white rounded-sm shadow-2xl w-full max-w-md overflow-hidden">
            <div className="px-5 py-4 bg-slate-900 text-white flex items-center justify-between">
              <h3 className="text-base font-black">Edit Purchase Party</h3>
              <button onClick={() => setSupEdit(null)} className="text-slate-400 hover:text-white text-2xl leading-none" data-testid="supplier-edit-close">×</button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <Label className="text-xs">Party Name *</Label>
                <Input value={supEdit.name} onChange={(e) => setSupEdit({ ...supEdit, name: e.target.value })} data-testid="supplier-edit-name" />
              </div>
              <div>
                <Label className="text-xs">GSTIN</Label>
                <Input value={supEdit.gstin} onChange={(e) => setSupEdit({ ...supEdit, gstin: e.target.value.toUpperCase() })} maxLength={15} data-testid="supplier-edit-gstin" />
              </div>
              <div data-testid="supplier-edit-group-picker">
                <Label className="text-xs">Under (Ledger Group) * — jaise Sundry Creditors / Sundry Debtors</Label>
                <GroupPicker groups={acctGroups} value={supEdit.group_id}
                  onChange={(g) => setSupEdit({ ...supEdit, group_id: g.id, group_name: g.name })}
                  onGroupCreated={(g) => setAcctGroups((gs) => [...gs, g].sort((a, b) => a.name.localeCompare(b.name)))}
                  testid="supplier-edit-group" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={() => setSupEdit(null)} data-testid="supplier-edit-cancel">Cancel</Button>
                <Button size="sm" onClick={saveSupplierEdit} disabled={supSaving} data-testid="supplier-edit-save" className="bg-slate-900 hover:bg-slate-800 text-white font-bold">{supSaving ? "Saving…" : "Save Party"}</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Party Reconcile chooser — Sales / Purchase / Expenses ledgers */}
      {reconTarget && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="party-reconcile-modal">
          <div className="bg-white rounded-sm shadow-2xl w-full max-w-2xl overflow-hidden">
            <div className="px-5 py-4 bg-slate-900 text-white flex items-start justify-between gap-3">
              <div>
                <p className="text-[10px] uppercase font-bold text-slate-400 tracking-widest">Reconcile Party Ledger</p>
                <h3 className="text-lg font-black mt-0.5">{reconTarget.name}</h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  {reconTarget.multi
                    ? `${reconTarget.multi.length} ledgers selected — sabki entries chuni hui ledger me settle hongi`
                    : reconPreview && !reconPreview.error
                    ? `${reconPreview.unlinked_txns} unlinked bank entries (${reconPreview.unlinked_credits} credits ₹${(reconPreview.unlinked_credit_amount || 0).toLocaleString()} · ${reconPreview.unlinked_debits} debits ₹${(reconPreview.unlinked_debit_amount || 0).toLocaleString()})`
                    : reconPreview?.error || "Loading…"}
                </p>
              </div>
              <button onClick={() => setReconTarget(null)} className="text-slate-400 hover:text-white text-2xl leading-none" data-testid="party-reconcile-close">×</button>
            </div>
            <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
              <p className="text-xs text-slate-500">Choose a ledger below — all entries of this party will settle into that ledger <b>bill-wise (exact amount)</b> first, then <b>date-wise</b> automatically. Repeated / already-mapped entries will be skipped.</p>
              <input value={ledgerSearch} onChange={(e) => setLedgerSearch(e.target.value)}
                placeholder="Search ledger name…" data-testid="recon-ledger-search"
                className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />

              {/* Suspense — send everything back for manual review */}
              <div data-testid="recon-suspense-option" className="border border-amber-300 bg-amber-50 rounded-sm px-3 py-2 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-bold text-amber-900">! Suspense</p>
                  <p className="text-[10px] text-amber-700">All entries of this party go back to Suspense — the entire settlement will be reversed</p>
                </div>
                <button onClick={movePartyToSuspense} disabled={reconRunning !== null}
                  data-testid="recon-to-suspense-btn"
                  className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded-sm text-[11px] font-bold disabled:opacity-40">
                  {reconRunning === "suspense" ? <ArrowClockwise size={11} className="animate-spin" /> : <LinkIcon size={11} weight="bold" />}
                  Move to Suspense
                </button>
              </div>

              {/* Party ledgers — ONLY sale/purchase parties (suspense excluded) */}
              <div data-testid="recon-party-ledgers">
                <p className="text-[10px] uppercase font-bold text-slate-500 tracking-widest mb-1.5">
                  Party Ledgers — Sales / Purchase · {(allParties?.parties || []).filter((p) => p.kind !== "suspense").length}
                </p>
                <div className="border border-slate-200 rounded-sm divide-y divide-slate-100 max-h-56 overflow-y-auto">
                  {(allParties?.parties || [])
                    .filter((p) => p.kind !== "suspense")
                    .filter((p) => !ledgerSearch || (p.name || "").toLowerCase().includes(ledgerSearch.toLowerCase()))
                    .map((p) => (
                      <div key={p.id} className="flex items-center justify-between gap-2 px-3 py-2 hover:bg-slate-50">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-900 truncate flex items-center gap-2">
                            {p.name}
                            {(p.kind === "supplier" || p.kind === "existing_supplier") && <span className="text-[9px] font-black px-1.5 py-0.5 rounded-sm bg-indigo-100 text-indigo-800 shrink-0">PURCHASE</span>}
                            {p.kind === "customer" && <span className="text-[9px] font-black px-1.5 py-0.5 rounded-sm bg-emerald-100 text-emerald-800 shrink-0">SALES</span>}
                          </p>
                          <p className="text-[10px] text-slate-400">₹{Number(p.billed || 0).toLocaleString("en-IN")} billed · ₹{Number(p.pending || 0).toLocaleString("en-IN")} pending</p>
                        </div>
                        <button onClick={() => runReconcileTo("party", p)}
                          disabled={reconRunning !== null}
                          data-testid={`recon-to-party-${p.id}`}
                          className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-sm text-[11px] font-bold disabled:opacity-40">
                          {reconRunning === `party:${p.id}` ? <ArrowClockwise size={11} className="animate-spin" /> : <LinkIcon size={11} weight="bold" />}
                          Settle Here
                        </button>
                      </div>
                    ))}
                  {(allParties?.parties || []).filter((p) => p.kind !== "suspense").length === 0 && (
                    <p className="px-3 py-4 text-xs text-slate-400 text-center">No party ledgers yet</p>
                  )}
                </div>
              </div>

              {/* Expense ledgers — Bank Charges, Cash Deposit, Petrol, etc. */}
              <div data-testid="recon-expense-ledgers">
                <p className="text-[10px] uppercase font-bold text-slate-500 tracking-widest mb-1.5">
                  Expense Ledgers · {expenseHeads.length}
                </p>
                <div className="border border-rose-200 rounded-sm divide-y divide-rose-100 max-h-44 overflow-y-auto bg-rose-50/40">
                  {expenseHeads
                    .filter((e) => !ledgerSearch || (e.name || "").toLowerCase().includes(ledgerSearch.toLowerCase()))
                    .map((e) => (
                      <div key={e.id} className="flex items-center justify-between gap-2 px-3 py-2 hover:bg-rose-50">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-900 truncate">{e.name}</p>
                          <p className="text-[10px] text-slate-400">{e.under_group || (e.category || "expense").replace(/_/g, " ")}</p>
                        </div>
                        <button onClick={() => runReconcileTo("expense", e)}
                          disabled={reconRunning !== null}
                          data-testid={`recon-to-expense-${e.id}`}
                          className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 bg-rose-600 hover:bg-rose-700 text-white rounded-sm text-[11px] font-bold disabled:opacity-40">
                          {reconRunning === `expense:${e.id}` ? <ArrowClockwise size={11} className="animate-spin" /> : <LinkIcon size={11} weight="bold" />}
                          Settle Here
                        </button>
                      </div>
                    ))}
                  {expenseHeads.length === 0 && (
                    <p className="px-3 py-4 text-xs text-slate-400 text-center">No expense ledgers yet — create one from the Expenses tab</p>
                  )}
                </div>
              </div>

              {reconResult && (
                <div className="border border-emerald-300 bg-emerald-50 rounded-sm px-4 py-3 text-xs font-semibold text-emerald-900" data-testid="recon-result">
                  <CheckCircle size={13} weight="fill" className="inline mr-1 text-emerald-600" />
                  → {reconResult.target}: {reconResult.invoices_linked} sale, {reconResult.purchases_linked} purchase, {reconResult.expenses_linked} expense settled ·{" "}
                  {reconResult.on_account || 0} on-account adjusted · {reconResult.duplicates_skipped} repeated skipped · {reconResult.unmatched_left} left
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
